import DonationPanel from '../components/DonationPanel';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHeader, StatCards, Card, TabBar, Table, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import { printBillReceipt, printComplimentaryAuthorization, printDailySalesSummary } from '../utils/print';
import { AreaChart, BarChartV, DonutRing, RankBars } from '../components/DashboardWidgets';

// ─── Approval Stamps SVG ─────────────────────────────────────────────────────
function _buildStampSVG(label, uid, size) {
  const cx = size / 2, cy = size / 2;
  const rOuter = cx - 3, rInner = cx - 10;
  const arcR = rOuter - 5;
  const fontSize = Math.max(4.5, size * 0.077);
  const mainFS   = Math.round(9 * (size / 72));
  return { cx, cy, rOuter, rInner, arcR, fontSize, mainFS, uid };
}

function ManagerStamp({ by, at, size = 76 }) {
  const { cx, cy, rOuter, rInner, arcR, fontSize, mainFS, uid } = _buildStampSVG('MANAGER', `mgr_s_${size}`, size);
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity:0.9 }}>
        <defs>
          <path id={`${uid}_top`} d={`M ${cx-arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}/>
          <filter id={`${uid}_d`}><feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9" xChannelSelector="R" yChannelSelector="G"/></filter>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2" filter={`url(#${uid}_d)`}/>
        <circle cx={cx} cy={cy} r={rInner} fill="none"    stroke="#8B1A1A" strokeWidth="1.2" filter={`url(#${uid}_d)`}/>
        <text fontSize={fontSize} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">GRAND MEMORIES</textPath>
        </text>
        <line x1={cx-rInner+5} y1={cy-rInner*0.40} x2={cx+rInner-5} y2={cy-rInner*0.40} stroke="#8B1A1A" strokeWidth="0.9"/>
        <line x1={cx-rInner+5} y1={cy+rInner*0.20} x2={cx+rInner-5} y2={cy+rInner*0.20} stroke="#8B1A1A" strokeWidth="0.9"/>
        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize={mainFS}
          fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="900" filter={`url(#${uid}_d)`}>MANAGER</text>
        <text x={cx} y={cy+rInner*0.38} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5,size*0.069)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="600" letterSpacing="1.8">— APPROVED —</text>
        <text x={cx-rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx+rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx} y={cy+rInner*0.85} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4,size*0.069)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="700" letterSpacing="1.6">RESORT &amp; SPA</text>
      </svg>
      {(by||at) && (
        <div style={{ fontSize:9, color:'#888', textAlign:'center', maxWidth:size, lineHeight:1.3 }}>
          {by && <div style={{ fontWeight:600 }}>{by}</div>}
          {at && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'})}</div>}
        </div>
      )}
    </div>
  );
}

function AccountantStampMini({ by, at, size = 76 }) {
  const { cx, cy, rOuter, rInner, arcR, fontSize, mainFS, uid } = _buildStampSVG('ACCOUNTANT', `acct_s_${size}`, size);
  const labelFS = Math.round(mainFS * 0.82);
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity:0.9 }}>
        <defs>
          <path id={`${uid}_top`} d={`M ${cx-arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}/>
          <filter id={`${uid}_d`}><feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9" xChannelSelector="R" yChannelSelector="G"/></filter>
        </defs>
        <circle cx={cx} cy={cy} r={rOuter} fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2" filter={`url(#${uid}_d)`}/>
        <circle cx={cx} cy={cy} r={rInner} fill="none"    stroke="#8B1A1A" strokeWidth="1.2" filter={`url(#${uid}_d)`}/>
        <text fontSize={fontSize} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">GRAND MEMORIES</textPath>
        </text>
        <line x1={cx-rInner+5} y1={cy-rInner*0.40} x2={cx+rInner-5} y2={cy-rInner*0.40} stroke="#8B1A1A" strokeWidth="0.9"/>
        <line x1={cx-rInner+5} y1={cy+rInner*0.20} x2={cx+rInner-5} y2={cy+rInner*0.20} stroke="#8B1A1A" strokeWidth="0.9"/>
        <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize={labelFS}
          fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="900" filter={`url(#${uid}_d)`}>ACCOUNTANT</text>
        <text x={cx} y={cy+rInner*0.38} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5,size*0.069)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="600" letterSpacing="1.8">— APPROVED —</text>
        <text x={cx-rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx+rInner*0.55} y={cy+rInner*0.66} textAnchor="middle" fontSize={Math.max(5,size*0.09)} fill="#8B1A1A">★</text>
        <text x={cx} y={cy+rInner*0.85} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4,size*0.069)} fill="#8B1A1A" fontFamily="'Arial Narrow',Arial,sans-serif" fontWeight="700" letterSpacing="1.6">RESORT &amp; SPA</text>
      </svg>
      {(by||at) && (
        <div style={{ fontSize:9, color:'#888', textAlign:'center', maxWidth:size, lineHeight:1.3 }}>
          {by && <div style={{ fontWeight:600 }}>{by}</div>}
          {at && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'})}</div>}
        </div>
      )}
    </div>
  );
}

const RETURN_REASONS = [
  { id:'wrong_item',  label:'Wrong Item Delivered' },
  { id:'defective',   label:'Defective / Damaged' },
  { id:'quality',     label:'Quality Complaint' },
  { id:'overcharge',  label:'Overcharged' },
  { id:'not_ordered', label:'Item Not Ordered' },
  { id:'other',       label:'Other' },
];
const RETURN_SOURCES = [
  { id:'restaurant',   label:'Restaurant' },
  { id:'bar',          label:'Bar' },
  { id:'gift_shop',    label:'Gift Shop' },
  { id:'room_service', label:'Room Service' },
  { id:'other',        label:'Other' },
];

const MODE_ICONS  = { cash:'💵', mpesa:'📱', bank:'🏦', pdq:'💳', 'room-charge':'🏨' };
const MODE_COLORS = { cash:'#2ecc71', mpesa:'#00b300', bank:'#3498db', pdq:'#9b59b6', 'room-charge':'#e67e22' };
const STATUS_COLORS = { pending:'#e67e22', 'payment-requested':'#3498db', paid:'#2ecc71' };

// Categories are now dynamic — loaded from backend. These are seeded defaults only if the DB is empty.
const DEFAULT_FOOD_CATS = ['Starters','Soup','Salads','Main Course','Grills','Seafood','Vegetarian','Pasta','Pizza','Burgers','Desserts','Kids Menu'];
const DEFAULT_BAR_CATS  = ['Cocktails','Mocktails','Beers','Wines','Spirits','Soft Drinks','Juices','Hot Drinks','Water'];

// Simple bar chart
function BarChart({ data, labelKey, valueKey, color='#c8a96e', height=120, fmtVal=v=>v }) {
  if (!data?.length) return <div style={{ color:'var(--textFaint)', fontSize:12, textAlign:'center', padding:20 }}>No data</div>;
  const max = Math.max(...data.map(d => d[valueKey]||0)) || 1;
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:4, height, paddingBottom:20, position:'relative' }}>
      {data.map((d, i) => {
        const pct = ((d[valueKey]||0) / max) * 100;
        return (
          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', height:'100%', justifyContent:'flex-end' }}>
            <div title={`${d[labelKey]}: ${fmtVal(d[valueKey])}`}
              style={{ width:'100%', height:`${pct}%`, background:color, borderRadius:'2px 2px 0 0', minHeight:2, transition:'height .3s', cursor:'default' }}/>
            <div style={{ fontSize:8, color:'var(--textFaint)', marginTop:3, textAlign:'center', transform:'rotate(-45deg)', whiteSpace:'nowrap', transformOrigin:'top left', marginLeft:6, lineHeight:1 }}>
              {String(d[labelKey]).slice(0,10)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Mini line sparkline
function Sparkline({ data, valueKey='revenue', color='#c8a96e', height=48 }) {
  if (!data?.length) return null;
  const vals = data.map(d=>d[valueKey]||0);
  const max = Math.max(...vals)||1, min = Math.min(...vals);
  const w = 200, h = height;
  const pts = vals.map((v,i) => {
    const x = (i/(vals.length-1||1))*w;
    const y = h - ((v-min)/(max-min||1))*(h-4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display:'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    </svg>
  );
}


const blankMenuItem = { name:'', category:'', menu_type:'food', price:'', cost_price:'', description:'', is_available:true };

// ─── Food Costing Calculator ──────────────────────────────────────────────────
const VAT_RATE          = 0.16;   // 16% VAT (Kenya)
const CATERING_LEVY     = 0.02;   // 2% Catering Levy
const SERVICE_CHARGE    = 0.10;   // 10% Service Charge

const blankIngredient = { name:'', qty:'', unit:'g', unit_cost:'', yield_pct:'100' };

function FoodCostingCalculator({ notify }) {
  const [dishName,       setDishName]       = useState('');
  const [portions,       setPortions]       = useState('1');
  const [ingredients,    setIngredients]    = useState([{ ...blankIngredient }]);
  const [overhead,       setOverhead]       = useState('20');  // % of ingredient cost
  const [profitMargin,   setProfitMargin]   = useState('35');  // desired GP %
  const [inclVAT,        setInclVAT]        = useState(true);
  const [inclLevy,       setInclLevy]       = useState(true);
  const [inclService,    setInclService]    = useState(false);
  const [roundTo,        setRoundTo]        = useState('5');   // round selling price to nearest N
  const [menuType,       setMenuType]       = useState('food');
  const [menuCategory,   setMenuCategory]   = useState('Hot Drinks');
  const [publishing,     setPublishing]     = useState(false);
  const [published,      setPublished]      = useState(null); // { name, price }

  const updateIng = (i, field, val) => {
    setIngredients(prev => prev.map((ing, idx) => idx===i ? { ...ing, [field]: val } : ing));
  };
  const addIng    = () => setIngredients(prev => [...prev, { ...blankIngredient }]);
  const removeIng = (i) => setIngredients(prev => prev.filter((_,idx) => idx!==i));

  // ── Calculations ─────────────────────────────────────────────────────────────
  const numPortions = Math.max(1, parseFloat(portions)||1);

  // Cost per ingredient after yield adjustment
  const ingCosts = ingredients.map(ing => {
    const qty      = parseFloat(ing.qty)        || 0;
    const unitCost = parseFloat(ing.unit_cost)  || 0;
    const yieldPct = Math.max(1, parseFloat(ing.yield_pct) || 100) / 100;
    const rawCost  = qty * unitCost;           // cost before yield
    const trueCost = rawCost / yieldPct;       // cost after yield (waste factored in)
    return { ...ing, rawCost, trueCost };
  });

  const totalIngCost    = ingCosts.reduce((s,i) => s + i.trueCost, 0);
  const overheadAmt     = totalIngCost * (parseFloat(overhead)||0) / 100;
  const totalFoodCost   = totalIngCost + overheadAmt;            // per batch
  const foodCostPer     = totalFoodCost / numPortions;           // per portion

  // Back-calculate selling price from desired GP margin
  // GP% = (Selling - FoodCost) / Selling  →  Selling = FoodCost / (1 - GP%)
  const gpFraction      = Math.min(0.99, (parseFloat(profitMargin)||0) / 100);
  const priceBeforeTax  = gpFraction >= 1 ? foodCostPer * 2 : foodCostPer / (1 - gpFraction);

  // Stack taxes/levies
  const vatAmt          = inclVAT     ? priceBeforeTax * VAT_RATE       : 0;
  const levyAmt         = inclLevy    ? priceBeforeTax * CATERING_LEVY  : 0;
  const serviceAmt      = inclService ? priceBeforeTax * SERVICE_CHARGE : 0;
  const priceWithTax    = priceBeforeTax + vatAmt + levyAmt + serviceAmt;

  // Round to nearest N
  const rn              = parseFloat(roundTo) || 1;
  const suggestedPrice  = Math.ceil(priceWithTax / rn) * rn;

  // Verify margins on the rounded price
  const actualGP        = suggestedPrice > 0 ? ((suggestedPrice - foodCostPer) / suggestedPrice) * 100 : 0;
  const foodCostPct     = suggestedPrice > 0 ? (foodCostPer / suggestedPrice) * 100 : 0;

  const fmtK = n => 'KES ' + n.toFixed(2);
  const inp  = { width:'100%', background:'#0e0e0e', border:'1px solid #2a2a2a', borderRadius:3, padding:'8px 10px', color:'#e0d0b0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box' };
  const lbl  = { fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:4, display:'block' };
  const chk  = { accentColor:'#c8a96e', width:14, height:14 };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12, alignItems:'start' }}>

      {/* ── LEFT: Inputs ── */}
      <div>
        <Card title="🧮 Recipe & Ingredients">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10, marginBottom:14 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label style={lbl}>Dish / Item Name</label>
              <input style={inp} placeholder="e.g. Cup of Tea" value={dishName} onChange={e=>setDishName(e.target.value)}/>
            </div>
            <div>
              <label style={lbl}>Number of Portions</label>
              <input style={inp} type="number" min="1" placeholder="1" value={portions} onChange={e=>setPortions(e.target.value)}/>
            </div>
            <div>
              <label style={lbl}>Overhead / Labour %</label>
              <input style={inp} type="number" min="0" max="200" placeholder="20" value={overhead} onChange={e=>setOverhead(e.target.value)}/>
              <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:3 }}>% added on top of raw ingredient cost</div>
            </div>
          </div>

          {/* Ingredients table */}
          <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Ingredients</div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 0.7fr 0.7fr 1fr 0.7fr 28px', gap:5, marginBottom:5, minWidth:480 }}>
            {['Ingredient','Qty','Unit','Cost/Unit (KES)','Yield %',''].map(h=>(
              <div key={h} style={{ fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {ingredients.map((ing, i) => (
            <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 0.7fr 0.7fr 1fr 0.7fr 28px', gap:5, marginBottom:6, minWidth:480 }}>
              <input style={inp} placeholder="e.g. Milk" value={ing.name} onChange={e=>updateIng(i,'name',e.target.value)}/>
              <input style={inp} type="number" min="0" placeholder="200" value={ing.qty} onChange={e=>updateIng(i,'qty',e.target.value)}/>
              <select style={inp} value={ing.unit} onChange={e=>updateIng(i,'unit',e.target.value)}>
                {['g','kg','ml','L','pcs','tbsp','tsp','cup'].map(u=><option key={u}>{u}</option>)}
              </select>
              <input style={inp} type="number" min="0" placeholder="0.50" value={ing.unit_cost} onChange={e=>updateIng(i,'unit_cost',e.target.value)}/>
              <input style={inp} type="number" min="1" max="100" placeholder="100" value={ing.yield_pct} onChange={e=>updateIng(i,'yield_pct',e.target.value)}/>
              <button onClick={()=>removeIng(i)} style={{ background:'#1a0a0a', border:'1px solid #3a1a1a', borderRadius:3, color:'#e74c3c', cursor:'pointer', fontSize:13 }}>✕</button>
            </div>
          ))}
          <button onClick={addIng} style={{ fontSize:11, padding:'6px 14px', background:'transparent', border:'1px solid #2a2a2a', borderRadius:3, color:'#c8a96e', cursor:'pointer', fontFamily:'inherit', marginTop:4 }}>
            + Add Ingredient
          </button>
        </Card>

        <Card title="💰 Pricing & Tax Settings" style={{ marginTop:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10, marginBottom:10 }}>
            <div>
              <label style={lbl}>Desired Profit Margin %</label>
              <input style={inp} type="number" min="0" max="99" placeholder="35" value={profitMargin} onChange={e=>setProfitMargin(e.target.value)}/>
              <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:3 }}>Gross profit as % of selling price</div>
            </div>
            <div>
              <label style={lbl}>Round Selling Price to Nearest</label>
              <select style={inp} value={roundTo} onChange={e=>setRoundTo(e.target.value)}>
                {['1','5','10','50','100'].map(v=><option key={v} value={v}>KES {v}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { label:'VAT (16%)',              state:inclVAT,     set:setInclVAT,     detail:'Kenya standard rate' },
              { label:'Catering Levy (2%)',     state:inclLevy,    set:setInclLevy,    detail:'Tourism Finance Corp' },
              { label:'Service Charge (10%)',   state:inclService, set:setInclService, detail:'Optional — goes to staff' },
            ].map(({ label, state, set, detail }) => (
              <label key={label} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', padding:'8px 10px', background:'#0e0e0e', border:'1px solid #1e1e1e', borderRadius:3 }}>
                <input type="checkbox" style={chk} checked={state} onChange={e=>set(e.target.checked)}/>
                <div>
                  <div style={{ fontSize:12, color:'#c0b090' }}>{label}</div>
                  <div style={{ fontSize:10, color:'var(--textFaint)' }}>{detail}</div>
                </div>
                <div style={{ marginLeft:'auto', fontSize:11, color: state?'#2ecc71':'#444' }}>{state?'Included':'Excluded'}</div>
              </label>
            ))}
          </div>
        </Card>
      </div>

      {/* ── RIGHT: Results ── */}
      <div>
        <Card title={`📋 Cost Breakdown${dishName ? ` — ${dishName}` : ''}`}>
          {/* Ingredient cost lines */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Ingredient Costs (per batch of {numPortions} portion{numPortions!==1?'s':''})</div>
            {ingCosts.map((ing, i) => {
              if (!ing.name && !ing.qty) return null;
              const yPct = Math.max(1, parseFloat(ing.yield_pct)||100);
              const wastePct = 100 - yPct;
              return (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #1a1a1a', fontSize:12 }}>
                  <div>
                    <span style={{ color:'#c0b090' }}>{ing.name||`Ingredient ${i+1}`}</span>
                    <span style={{ color:'var(--textFaint)', fontSize:10, marginLeft:8 }}>{ing.qty}{ing.unit} @ KES {parseFloat(ing.unit_cost||0).toFixed(2)}/{ing.unit}</span>
                    {wastePct > 0 && <span style={{ color:'#e67e22', fontSize:10, marginLeft:8 }}>yield {yPct}% (+{wastePct}% waste)</span>}
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ color:'#c8a96e', fontWeight:600 }}>{fmtK(ing.trueCost)}</div>
                    {wastePct > 0 && <div style={{ color:'var(--textFaint)', fontSize:10 }}>raw: {fmtK(ing.rawCost)}</div>}
                  </div>
                </div>
              );
            })}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Total Ingredient Cost</span>
              <span style={{ color:'#c8a96e', fontWeight:700 }}>{fmtK(totalIngCost)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Overhead / Labour ({overhead}%)</span>
              <span style={{ color:'#c8a96e' }}>{fmtK(overheadAmt)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderTop:'1px solid #2a2a2a', fontSize:13, fontWeight:700 }}>
              <span style={{ color:'var(--textMuted)' }}>Total Food Cost (batch)</span>
              <span style={{ color:'#e0d0b0' }}>{fmtK(totalFoodCost)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Food Cost per Portion</span>
              <span style={{ color:'#e0d0b0', fontWeight:600 }}>{fmtK(foodCostPer)}</span>
            </div>
          </div>

          {/* Pricing build-up */}
          <div style={{ background:'var(--bg)', border:'1px solid #1e1e1e', borderRadius:4, padding:14 }}>
            <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:10 }}>Selling Price Build-Up (per portion)</div>
            {[
              { label:`Food Cost`,                        val: foodCostPer,     color:'#e74c3c' },
              { label:`Gross Profit (${profitMargin}% margin)`, val: priceBeforeTax - foodCostPer, color:'#2ecc71' },
              { label:`Pre-tax Price`,                    val: priceBeforeTax,  color:'#c8a96e', bold:true },
              inclVAT     && { label:`VAT (16%)`,         val: vatAmt,          color:'#e67e22' },
              inclLevy    && { label:`Catering Levy (2%)`,val: levyAmt,         color:'#e67e22' },
              inclService && { label:`Service Charge (10%)`, val: serviceAmt,   color:'#e67e22' },
            ].filter(Boolean).map(({ label, val, color, bold }) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #151515', fontSize:12 }}>
                <span style={{ color:'var(--textMuted)' }}>{label}</span>
                <span style={{ color, fontWeight: bold?700:400 }}>{fmtK(val)}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:12 }}>
              <span style={{ color:'var(--textMuted)' }}>Price with taxes</span>
              <span style={{ color:'#c8a96e' }}>{fmtK(priceWithTax)}</span>
            </div>
          </div>

          {/* Final suggested price */}
          <div style={{ background:'#0d1a0d', border:'1px solid #2ecc7133', borderRadius:4, padding:18, marginTop:12, textAlign:'center' }}>
            <div style={{ fontSize:10, color:'#2ecc71', letterSpacing:3, textTransform:'uppercase', marginBottom:6 }}>Suggested Selling Price</div>
            <div style={{ fontSize:42, fontWeight:700, color:'#2ecc71', letterSpacing:2 }}>KES {suggestedPrice.toLocaleString('en-KE')}</div>
            <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:4 }}>Rounded to nearest KES {roundTo}</div>
          </div>

          {/* Margin verification */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:8, marginTop:12 }}>
            {[
              { label:'Food Cost %',   val:`${foodCostPct.toFixed(1)}%`,  color: foodCostPct>40?'#e74c3c':foodCostPct>30?'#e67e22':'#2ecc71' },
              { label:'Gross Profit %',val:`${actualGP.toFixed(1)}%`,     color: actualGP<25?'#e74c3c':actualGP<35?'#e67e22':'#2ecc71' },
              { label:'Per Portion Cost', val:fmtK(foodCostPer),          color:'#c8a96e' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background:'#0e0e0e', border:'1px solid #1e1e1e', borderRadius:4, padding:'10px 12px', textAlign:'center' }}>
                <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:18, fontWeight:700, color }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Industry guidance */}
          <div style={{ marginTop:12, padding:'10px 14px', background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:4, fontSize:11, color:'var(--textMuted)', lineHeight:1.7 }}>
            <div style={{ color:'#c8a96e', fontWeight:600, marginBottom:4 }}>Industry Benchmarks</div>
            Food cost should ideally be <span style={{ color:'#2ecc71' }}>25–35%</span> of selling price. &nbsp;
            Gross profit margin of <span style={{ color:'#2ecc71' }}>65–75%</span> is healthy for a hotel restaurant. &nbsp;
            If food cost exceeds <span style={{ color:'#e74c3c' }}>40%</span>, review recipe yield, supplier pricing, or raise the selling price.
          </div>

          {/* ── Publish to Menu ── */}
          <div style={{ marginTop:12, background:'#080e08', border:'1px solid #2ecc7133', borderRadius:4, padding:16 }}>
            <div style={{ fontSize:11, color:'#2ecc71', letterSpacing:2, textTransform:'uppercase', marginBottom:10 }}>📋 Publish to Waiter Menu</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:8, marginBottom:10 }}>
              <div>
                <label style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:4, display:'block' }}>Menu Section</label>
                <select style={{ width:'100%', background:'#0e0e0e', border:'1px solid #2a2a2a', borderRadius:3, padding:'8px 10px', color:'#e0d0b0', fontSize:13, fontFamily:'inherit' }}
                  value={menuType} onChange={e=>setMenuType(e.target.value)}>
                  <option value="food">🍽 Food Menu</option>
                  <option value="bar">🍺 Bar Menu</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:4, display:'block' }}>Category on Menu</label>
                <select style={{ width:'100%', background:'#0e0e0e', border:'1px solid #2a2a2a', borderRadius:3, padding:'8px 10px', color:'#e0d0b0', fontSize:13, fontFamily:'inherit' }}
                  value={menuCategory} onChange={e=>setMenuCategory(e.target.value)}>
                  {(menuType==='food'
                    ? ['Breakfast','Starters','Main Course','Salads','Soups','Snacks','Desserts','Hot Drinks','Other']
                    : ['Soft Drinks','Beers','Wines','Spirits','Cocktails','Hot Drinks','Water','Other']
                  ).map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ fontSize:11, color:'var(--textFaint)', marginBottom:10 }}>
              This will create <strong style={{ color:'#c0b090' }}>{dishName||'the dish'}</strong> on the <strong style={{ color:'#c0b090' }}>{menuType==='food'?'Food':'Bar'} menu</strong> at <strong style={{ color:'#2ecc71' }}>KES {suggestedPrice.toLocaleString('en-KE')}</strong> so waiters can immediately add it to orders.
            </div>
            {published && (
              <div style={{ padding:'8px 12px', background:'#0a1a0a', border:'1px solid #2ecc7144', borderRadius:3, marginBottom:10, fontSize:12, color:'#2ecc71' }}>
                ✓ <strong>{published.name}</strong> is live on the menu at KES {published.price.toLocaleString('en-KE')}
              </div>
            )}
            <button
              disabled={!dishName || suggestedPrice<=0 || publishing}
              style={{ width:'100%', background: (!dishName||suggestedPrice<=0)?'var(--bgInput)':'#1a3a1a', border:`1px solid ${(!dishName||suggestedPrice<=0)?'var(--borderMid)':'#2ecc7155'}`, borderRadius:3, padding:'11px', color:(!dishName||suggestedPrice<=0)?'#444':'#2ecc71', fontSize:13, fontWeight:700, cursor:(!dishName||suggestedPrice<=0)?'not-allowed':'pointer', fontFamily:'inherit', letterSpacing:1 }}
              onClick={async () => {
                if (!dishName) { notify('Enter a dish name first','error'); return; }
                if (suggestedPrice <= 0) { notify('Calculate a price first','error'); return; }
                setPublishing(true);
                try {
                  const res = await api.createMenuItem({
                    name: dishName,
                    price: suggestedPrice,
                    cost_price: foodCostPer,
                    category: menuCategory,
                    menu_type: menuType,
                    description: `Food cost: KES ${foodCostPer.toFixed(2)} | GP: ${actualGP.toFixed(1)}%`,
                    is_available: true,
                  });
                  setPublished({ name: res.data.name, price: suggestedPrice });
                  notify(`✓ "${dishName}" published to ${menuType==='food'?'Food':'Bar'} menu at KES ${suggestedPrice.toLocaleString('en-KE')}!`);
                } catch(e) {
                  notify(e.response?.data?.name?.[0] || 'Failed to publish to menu','error');
                } finally { setPublishing(false); }
              }}>
              {publishing ? '⏳ Publishing…' : `📋 Add "${dishName||'Dish'}" to ${menuType==='food'?'Food':'Bar'} Menu — KES ${suggestedPrice.toLocaleString('en-KE')}`}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function RestaurantManagerPage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab] = useState('dashboard');
  const [period, setPeriod] = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  // Data
  const [reports, setReports]   = useState(null);
  const [orders, setOrders]     = useState([]);
  const [bills, setBills]       = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading]   = useState(false);

  // Returns state
  const [returns, setReturns]         = useState([]);
  const [returnFilter, setReturnFilter] = useState('all');
  const [returnModal, setReturnModal] = useState(false);
  const [returnForm, setReturnForm]   = useState({ item_name:'', source:'restaurant', reason:'quality', quantity:'1', unit_price:'', customer_name:'', description:'' });
  const [approveModal, setApproveModal] = useState(null);
  const [approveRes, setApproveRes]   = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectRes, setRejectRes]     = useState('');
  const [menuSearch, setMenuSearch] = useState('');
  const [menuTab, setMenuTab]       = useState('food');
  const [menuModal, setMenuModal]   = useState(false);
  const [editMenu, setEditMenu]     = useState(null);
  const [menuForm, setMenuForm]     = useState(blankMenuItem);

  // Category management state
  const [menuCategories, setMenuCategories]       = useState([]);
  const [catModal, setCatModal]                   = useState(false);
  const [editCat, setEditCat]                     = useState(null);
  const [catForm, setCatForm]                     = useState({ name:'', menu_type:'food', sort_order:0 });
  const [catSaving, setCatSaving]                 = useState(false);

  // Orders management
  const [orderFilter, setOrderFilter] = useState('all');
  const [voidModal, setVoidModal]     = useState(null);
  const [voidReason, setVoidReason]   = useState('');

  // Bill actions
  const [discModal, setDiscModal]   = useState(null);
  const [discPct, setDiscPct]       = useState('');
  const [discReason, setDiscReason] = useState('');
  const [refundModal, setRefundModal] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Shopping lists
  const [shoppingLists, setShoppingLists]   = useState([]);
  const [slRejectModal, setSlRejectModal]   = useState(null);
  const [slRejectReason, setSlRejectReason] = useState('');
  const [slDetail, setSlDetail]             = useState(null);

  // Complimentary bills (manager approves/rejects)
  const [compBills, setCompBills]             = useState([]);
  const [compFilter, setCompFilter]           = useState('pending');
  const [compSelected, setCompSelected]       = useState(null);
  const [compRejectModal, setCompRejectModal] = useState(null);
  const [compRejectReason, setCompRejectReason] = useState('');

  // Returns (continued)
  const [actionModal, setActionModal]   = useState(null);
  const [actionType, setActionType]     = useState('');
  const [actionResolution, setActionResolution] = useState('');
  const [returnDetail, setReturnDetail] = useState(null);

  const periodParams = useMemo(() => {
    let p = `?period=${period}`;
    if (period === 'custom' && dateFrom && dateTo) p += `&date_from=${dateFrom}&date_to=${dateTo}`;
    return p;
  }, [period, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rep, ord, bil, menu, logs, rets, sls, comp, cats] = await Promise.allSettled([
        api.getManagerReports(periodParams),
        api.getOrders(),
        api.getBills(),
        api.getAllMenu(),
        api.getAuditLogs('?action=order_voided&action=discount_applied&action=refund_issued&action=bill_paid&action=menu_item_created&action=menu_item_updated&action=price_changed'),
        api.getReturns(),
        api.getShoppingLists(),
        api.getComplimentaryBills(),
        api.getMenuCategories(),
      ]);
      const val = (r, fallback=[]) => r.status === 'fulfilled' ? (r.value.data.results || r.value.data) : fallback;
      setReports(rep.status === 'fulfilled' ? rep.value.data : {});
      setOrders(val(ord));
      setBills(val(bil));
      setMenuItems(val(menu));
      setAuditLogs(val(logs));
      setReturns(val(rets));
      setShoppingLists(val(sls));
      setCompBills(val(comp));
      setMenuCategories(val(cats));
    } catch(e) {
      notify('Error loading data', 'error');
    } finally { setLoading(false); }
  }, [periodParams, notify]);

  useEffect(() => { load(); }, [load]);

  // ─── Menu CRUD ──────────────────────────────────────────────────────────────
  const openMenuModal = (item=null) => {
    if (item) {
      setEditMenu(item);
      setMenuForm({ name:item.name, category:item.category||'', menu_type:item.menu_type||'food', price:String(item.price), cost_price:item.cost_price?String(item.cost_price):'', description:item.description||'', is_available:item.is_available!==false });
    } else {
      setEditMenu(null);
      setMenuForm({ ...blankMenuItem, menu_type: menuTab });
    }
    setMenuModal(true);
  };

  const saveMenuItem = async () => {
    if (!menuForm.name || !menuForm.price || !menuForm.category) { notify('Name, category and price required','error'); return; }
    if (parseFloat(menuForm.price) <= 0) { notify('Price must be positive','error'); return; }
    try {
      const payload = { ...menuForm, price: parseFloat(menuForm.price), cost_price: menuForm.cost_price ? parseFloat(menuForm.cost_price) : null };
      if (editMenu) await api.updateMenuItem(editMenu.id, payload);
      else await api.createMenuItem(payload);
      setMenuModal(false);
      await load();
      notify(editMenu ? 'Menu item updated!' : 'Menu item added!');
    } catch(e) { notify(e.response?.data?.name?.[0] || 'Error saving item','error'); }
  };

  const deleteMenuItem = async (id) => {
    if (!window.confirm('Remove this menu item?')) return;
    await api.deleteMenuItem(id);
    await load();
    notify('Menu item removed');
  };

  const toggleAvailability = async (item) => {
    await api.updateMenuItem(item.id, { is_available: !item.is_available });
    await load();
    notify(`${item.name} ${item.is_available ? 'deactivated' : 'activated'}`);
  };

  // ─── Category CRUD ──────────────────────────────────────────────────────────
  const openCatModal = (cat = null) => {
    if (cat) {
      setEditCat(cat);
      setCatForm({ name: cat.name, menu_type: cat.menu_type, sort_order: cat.sort_order || 0 });
    } else {
      setEditCat(null);
      setCatForm({ name: '', menu_type: menuTab, sort_order: 0 });
    }
    setCatModal(true);
  };

  const saveCat = async () => {
    if (!catForm.name.trim()) { notify('Category name is required', 'error'); return; }
    setCatSaving(true);
    try {
      if (editCat) await api.updateMenuCategory(editCat.id, catForm);
      else await api.createMenuCategory(catForm);
      setCatModal(false);
      await load();
      notify(editCat ? 'Category updated!' : 'Category created!');
    } catch(e) {
      notify(e.response?.data?.name?.[0] || 'Error saving category', 'error');
    } finally { setCatSaving(false); }
  };

  const deleteCat = async (cat) => {
    const itemsInCat = menuItems.filter(i => i.category === cat.name).length;
    if (itemsInCat > 0) { notify(`Cannot delete — ${itemsInCat} menu item(s) still use this category`, 'error'); return; }
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    await api.deleteMenuCategory(cat.id);
    await load();
    notify('Category deleted');
  };

  // ─── Order actions ──────────────────────────────────────────────────────────
  const voidOrder = async () => {
    if (!voidReason.trim()) { notify('Void reason required','error'); return; }
    try {
      await api.voidBill(voidModal.bill_id || voidModal.id, { reason: voidReason });
      setVoidModal(null); setVoidReason('');
      await load();
      notify('Order voided and logged');
    } catch(e) { notify(e.response?.data?.error || 'Error voiding order','error'); }
  };

  // ─── Bill actions ────────────────────────────────────────────────────────────
  const applyDiscount = async () => {
    const pct = parseFloat(discPct);
    if (!pct || pct <= 0 || pct > 100) { notify('Enter valid discount % (1-100)','error'); return; }
    if (!discReason.trim()) { notify('Discount reason required','error'); return; }
    try {
      await api.applyDiscount(discModal.id, { discount_pct: pct, reason: discReason });
      setDiscModal(null); setDiscPct(''); setDiscReason('');
      await load();
      notify(`${pct}% discount applied`);
    } catch(e) { notify(e.response?.data?.error || 'Error applying discount','error'); }
  };

  const processRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { notify('Enter valid refund amount','error'); return; }
    if (!refundReason.trim()) { notify('Refund reason required','error'); return; }
    try {
      await api.refundBill(refundModal.id, { amount: amt, reason: refundReason });
      setRefundModal(null); setRefundAmount(''); setRefundReason('');
      await load();
      notify('Refund processed');
    } catch(e) { notify(e.response?.data?.error || 'Error processing refund','error'); }
  };

  const reopenBill = async (bill) => {
    if (!window.confirm(`Reopen Bill #${bill.id}?`)) return;
    try {
      await api.reopenBill(bill.id);
      await load();
      notify(`Bill #${bill.id} reopened`);
    } catch(e) { notify(e.response?.data?.error || 'Error reopening','error'); }
  };

  // ─── Returns ─────────────────────────────────────────────────────────────────
  const submitReturn = async () => {
    if (!returnForm.item_name || !returnForm.unit_price || !returnForm.description) { notify('Item, price and description required','error'); return; }
    if (parseFloat(returnForm.unit_price) <= 0) { notify('Price must be positive','error'); return; }
    try {
      await api.createReturn({
        ...returnForm,
        quantity: parseFloat(returnForm.quantity)||1,
        unit_price: parseFloat(returnForm.unit_price),
      });
      setReturnModal(false);
      setReturnForm({ item_name:'', source:'restaurant', reason:'quality', quantity:'1', unit_price:'', customer_name:'', description:'' });
      await load();
      notify('Return note created and submitted for review');
    } catch(e) { notify(e.response?.data?.error || 'Error creating return','error'); }
  };

  const doApprove = async () => {
    try {
      await api.approveReturn(approveModal.id, { resolution: approveRes });
      setApproveModal(null); setApproveRes('');
      await load();
      notify(`Return ${approveModal.ref} approved`);
    } catch(e) { notify(e.response?.data?.error || 'Error approving return','error'); }
  };

  const doReject = async () => {
    if (!rejectRes.trim()) { notify('Please provide a rejection reason','error'); return; }
    try {
      await api.rejectReturn(rejectModal.id, { resolution: rejectRes });
      setRejectModal(null); setRejectRes('');
      await load();
      notify(`Return ${rejectModal.ref} rejected`);
    } catch(e) { notify(e.response?.data?.error || 'Error rejecting return','error'); }
  };

  // ─── Derived data ────────────────────────────────────────────────────────────
  const activeOrders  = orders.filter(o => !o.is_voided);
  const voidedOrders  = orders.filter(o => o.is_voided);
  const displayedOrders = orderFilter==='voided' ? voidedOrders : orderFilter==='active' ? activeOrders : orders;
  const paidBills     = bills.filter(b => b.status==='paid');
  const pendingBills  = bills.filter(b => b.status!=='paid');
  const discountedBills = bills.filter(b => parseFloat(b.discount_pct||0)>0);
  const refundedBills = bills.filter(b => b.is_refunded);

  const pendingReturns   = returns.filter(r => r.status==='pending');
  const approvedReturns  = returns.filter(r => r.status==='approved');
  const rejectedReturns  = returns.filter(r => r.status==='rejected');
  const totalReturnValue = returns.filter(r => r.status === 'approved').reduce((s,r) => s + parseFloat(r.total_value||0), 0);
  const displayedReturns = returnFilter==='pending' ? pendingReturns : returnFilter==='approved' ? approvedReturns : returnFilter==='rejected' ? rejectedReturns : returns;

  const filteredMenu  = menuItems.filter(i =>
    i.menu_type === menuTab &&
    (!menuSearch || i.name.toLowerCase().includes(menuSearch.toLowerCase()) || i.category.toLowerCase().includes(menuSearch.toLowerCase()))
  );

  // Derive dynamic category name lists from backend, falling back to defaults if none loaded yet
  const foodCatNames = menuCategories.filter(c => c.menu_type === 'food' && c.is_active).map(c => c.name);
  const barCatNames  = menuCategories.filter(c => c.menu_type === 'bar'  && c.is_active).map(c => c.name);
  const FOOD_CATS = foodCatNames.length ? foodCatNames : DEFAULT_FOOD_CATS;
  const BAR_CATS  = barCatNames.length  ? barCatNames  : DEFAULT_BAR_CATS;

  const catOptions = menuTab === 'food' ? FOOD_CATS : BAR_CATS;
  const margin = (item) => item.cost_price && item.price ? Math.round((1 - parseFloat(item.cost_price)/parseFloat(item.price))*100) : null;

  const rev = reports?.revenue;
  const ord = reports?.orders;

  return (
    <div>
      <PageHeader icon="🍽️" title="Restaurant Manager" sub="Sales oversight · Menu control · Staff performance · Analytics"/>

      {/* Period filter */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:11, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>Period:</span>
        {['today','week','month','custom'].map(p=>(
          <button key={p} onClick={()=>setPeriod(p)}
            style={{ fontSize:11, padding:'5px 12px', borderRadius:2, fontFamily:'inherit', cursor:'pointer', textTransform:'uppercase', letterSpacing:1,
              border:`1px solid ${period===p?'#c8a96e':'var(--borderMid)'}`, background:period===p?'#c8a96e22':'transparent', color:period===p?'#c8a96e':'#555' }}>
            {p}
          </button>
        ))}
        {period==='custom' && (
          <>
            <input type="date" style={{ ...inputStyle, width:140, padding:'5px 8px' }} value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
            <span style={{ color:'var(--textFaint)' }}>→</span>
            <input type="date" style={{ ...inputStyle, width:140, padding:'5px 8px' }} value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
          </>
        )}
        {loading && <span style={{ color:'var(--textFaint)', fontSize:11 }}>↻ Loading…</span>}
      </div>

      {/* Stat cards */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
        <button
          style={{ background:'var(--bgWarning)', border:'1px solid #c8a96e44', borderRadius:6, padding:'7px 16px', color:'#c8a96e', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}
          onClick={() => printDailySalesSummary({
            date: new Date().toLocaleDateString('en-KE', { dateStyle:'full' }),
            totalRevenue: rev?.total || 0,
            billCount: bills.length,
            paidCount: paidBills.length,
            pendingCount: pendingBills.length,
            byMode: Object.entries(reports?.payment_methods || {}).map(([mode, amount]) => ({ mode, amount, count: bills.filter(b => b.payment_mode === mode).length })),
            byWaiter: reports?.waiter_stats || [],
          })}>
          🖨 Print Daily Summary
        </button>
      </div>
      {rev && (
        <StatCards stats={[
          { label:'F&B Revenue',   value:fmtKES(rev.total),         color:'#c8a96e' },
          { label:'Food Revenue',  value:fmtKES(rev.food),          color:'#e67e22' },
          { label:'Bar Revenue',   value:fmtKES(rev.bar),           color:'#9b59b6' },
          { label:'Total Orders',  value:ord?.total||0,             color:'#3498db' },
          { label:'Avg Order',     value:fmtKES(ord?.avg_value||0), color:'#2ecc71' },
          { label:'Voided Orders', value:ord?.voided||0,            color:'#e74c3c' },
        ]}/>
      )}

      <TabBar tabs={[
        { id:'dashboard',   label:'Dashboard',        icon:'📊' },
        { id:'orders',      label:`Orders (${orders.length})`, icon:'🧾' },
        { id:'bills',       label:`Bills (${bills.length})`,   icon:'💰' },
        { id:'menu',        label:'Menu & Pricing',   icon:'📋' },
        { id:'staff',       label:'Staff Performance', icon:'👤' },
        { id:'analytics',   label:'Analytics',        icon:'📈' },
        { id:'returns',     label:`Returns (${pendingReturns.length} pending)`, icon:'↩️' },
        { id:'shopping',    label:`Shopping Lists (${shoppingLists.filter(s=>s.status==='pending').length} pending)`, icon:'🛒' },
        { id:'complimentary', label:`Complimentaries${compBills.filter(c=>c.status==='pending').length > 0 ? ` (${compBills.filter(c=>c.status==='pending').length} pending)` : ''}`, icon:'🎁' },
        { id:'costing',     label:'Food Costing',     icon:'🧮' },
        { id:'activity',    label:'Activity Log',     icon:'🔍' },
        { id:'donations', label:'Donations', icon:'🌿' },
      ]} active={tab} onChange={setTab}/>

      {/* ══════════════════════════════ DASHBOARD ══════════════════════════════ */}
      {tab==='dashboard' && reports && (
        <div style={{ display:'grid', gap:12 }}>

          {/* KPI strip */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:10 }}>
            {[
              { label:'F&B Revenue',    value:fmtKES(rev?.total||0),         color:'#c8a96e', icon:'💰' },
              { label:'Food Revenue',   value:fmtKES(rev?.food||0),          color:'#e67e22', icon:'🍽️' },
              { label:'Bar Revenue',    value:fmtKES(rev?.bar||0),           color:'#9b59b6', icon:'🍸' },
              { label:'Total Orders',   value:ord?.total||0,                 color:'#3498db', icon:'📋' },
              { label:'Avg Order Value',value:fmtKES(ord?.avg_value||0),     color:'#2ecc71', icon:'📊' },
              { label:'Gross Margin',   value:`${reports.food_cost?.gross_margin_pct||0}%`, color:'#c45e3e', icon:'📈' },
            ].map((s,i)=>(
              <div key={i} style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderTop:`3px solid ${s.color}`, borderRadius:12, padding:'16px 18px', position:'relative', overflow:'hidden' }}
                onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 4px 20px ${s.color}22`}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                <div style={{ position:'absolute', top:-20, right:-12, fontSize:40, opacity:0.06 }}>{s.icon}</div>
                <div style={{ fontSize:10, color:'var(--textMuted)', fontWeight:700, textTransform:'uppercase', letterSpacing:1.5, marginBottom:6 }}>{s.label}</div>
                <div style={{ fontSize:22, fontWeight:800, color:'var(--textPrimary)', letterSpacing:-0.5 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Row 2: Daily trend + peak hours + payment breakdown */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 220px 220px', gap:12 }}>
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Daily Revenue Trend</div>
              <AreaChart
                data={(reports.daily_trend||[]).slice(-21).map(d=>({ label:String(d.date||'').slice(5), value:parseFloat(d.revenue||0) }))}
                color="#c8a96e" height={140}
                formatY={v=>'K'+Math.round(v/1000)}
              />
            </div>
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Peak Hours</div>
              <BarChartV
                data={(reports.peak_hours||[]).map(h=>({ label:`${h.hour}h`, value:h.count||0 }))}
                color="#3498db" height={140}
              />
            </div>
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Payment Modes</div>
              <DonutRing
                segments={Object.entries(reports.payment_methods||{}).filter(([,v])=>v>0).slice(0,5).map(([k,v])=>({
                  label:k.replace('-',' '), value:v,
                  color:{cash:'#2ecc71',mpesa:'#00b300',pdq:'#9b59b6',bank:'#3498db','room-charge':'#e67e22',complimentary:'#c8a96e'}[k]||'#888'
                }))}
                size={110} stroke={12}
              />
            </div>
          </div>

          {/* Row 3: Top items + Category performance + F&B stats */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 200px', gap:12 }}>
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Top Menu Items</div>
              <RankBars
                items={(reports.item_sales||[]).slice(0,8).map(item=>({ label:item.name, value:parseFloat(item.revenue||0), color:'#c8a96e' }))}
                formatVal={v=>'K'+Math.round(v/1000)}
                color="#c8a96e"
              />
            </div>
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Category Performance</div>
              <RankBars
                items={(reports.category_sales||[]).slice(0,7).map(cat=>({ label:cat.item__category||'—', value:parseFloat(cat.revenue||0) }))}
                formatVal={v=>'K'+Math.round(v/1000)}
                color="#e67e22"
              />
            </div>
            <div style={{ background:'var(--bgCard)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>F&B Health</div>
              {[
                ['Food Cost %', `${reports.food_cost?.food_cost_pct||0}%`, reports.food_cost?.food_cost_pct>35?'#e74c3c':'#2ecc71'],
                ['Gross Profit', fmtKES(reports.food_cost?.gross_profit||0), '#c8a96e'],
                ['Voided Orders', ord?.voided||0, '#e74c3c'],
                ['Discounts', reports.discounts?.count||0, '#e67e22'],
                ['Refunds', reports.refunds?.count||0, '#e74c3c'],
              ].map(([l,v,col],i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:11, color:'var(--textMuted)' }}>{l}</span>
                  <span style={{ fontSize:12, fontWeight:800, color:col }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

            {/* ══════════════════════════════ ORDERS ══════════════════════════════════ */}
      {tab==='orders' && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
            {[['all','All',orders.length],['active','Active',activeOrders.length],['voided','Voided',voidedOrders.length]].map(([f,l,n])=>(
              <button key={f} onClick={()=>setOrderFilter(f)}
                style={{ fontSize:11, padding:'5px 12px', borderRadius:2, fontFamily:'inherit', cursor:'pointer',
                  border:`1px solid ${orderFilter===f?'#c8a96e':'var(--borderMid)'}`, background:orderFilter===f?'#c8a96e22':'transparent',
                  color:orderFilter===f?'#c8a96e':'#666', textTransform:'uppercase', letterSpacing:1 }}>
                {l} ({n})
              </button>
            ))}
          </div>

          <Card title={`Orders (${displayedOrders.length})`}>
            {displayedOrders.length===0 ? <Empty text="No orders"/> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>{['#','Waiter','Table','Type','Items','Total','Status','Created','Actions'].map(h=>(
                      <th key={h} style={{ background:'#0e0e0e', padding:'7px 9px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {[...displayedOrders].reverse().map(o=>(
                      <tr key={o.id} style={{ borderBottom:'1px solid #1a1a1a', opacity:o.is_voided?0.5:1 }}>
                        <td style={{ padding:'8px 9px', color:'var(--textFaint)', fontFamily:'monospace' }}>#{o.id}</td>
                        <td style={{ padding:'8px 9px', color:'#3498db', fontWeight:600 }}>{o.waiter_name||'—'}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{o.table_number}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)', fontSize:10, textTransform:'uppercase' }}>{o.order_type}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{(o.items||[]).length} item{(o.items||[]).length!==1?'s':''}</td>
                        <td style={{ padding:'8px 9px', fontWeight:700, color:'#c8a96e' }}>{fmtKES(o.total)}</td>
                        <td style={{ padding:'8px 9px' }}>
                          {o.is_voided
                            ? <span style={{ color:'#e74c3c', fontSize:11, fontWeight:700 }}>VOIDED</span>
                            : <span style={{ color:'#2ecc71', fontSize:10 }}>{o.kitchen_status}</span>}
                        </td>
                        <td style={{ padding:'8px 9px', color:'var(--textFaint)', fontSize:11, whiteSpace:'nowrap' }}>{new Date(o.created_at).toLocaleString('en-KE')}</td>
                        <td style={{ padding:'8px 9px' }}>
                          {!o.is_voided && (
                            <button style={{ ...btnSm, fontSize:11, color:'#e74c3c', background:'#1a0a0a' }}
                              onClick={()=>{ setVoidModal(o); setVoidReason(''); }}>Void</button>
                          )}
                          {o.is_voided && (
                            <div style={{ fontSize:10, color:'var(--textFaint)' }}>
                              <div>{o.voided_by_name||'—'}</div>
                              <div style={{ color:'#444' }}>{o.void_reason?.slice(0,30)}</div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ══════════════════════════════ BILLS ═══════════════════════════════════ */}
      {tab==='bills' && (
        <>
          {/* Summary row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:8, marginBottom:12 }}>
            {[
              ['Total Bills', bills.length, '#3498db'],
              ['Paid', paidBills.length, '#2ecc71'],
              ['Discounted', discountedBills.length, '#e67e22'],
              ['Refunded', refundedBills.length, '#e74c3c'],
            ].map(([l,v,c])=>(
              <div key={l} style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:'12px 14px' }}>
                <div style={{ fontSize:10, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:22, fontWeight:700, color:c }}>{v}</div>
              </div>
            ))}
          </div>

          <Card title="All Bills">
            {bills.length===0 ? <Empty text="No bills yet"/> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>{['#','Waiter','Guest','Total','Disc%','Final','Mode','Status','Printed','Actions'].map(h=>(
                      <th key={h} style={{ background:'#0e0e0e', padding:'7px 9px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {[...bills].reverse().map(b=>(
                      <tr key={b.id} style={{ borderBottom:'1px solid #1a1a1a' }}>
                        <td style={{ padding:'8px 9px', color:'var(--textFaint)', fontFamily:'monospace' }}>#{b.id}</td>
                        <td style={{ padding:'8px 9px', color:'#3498db', fontWeight:600 }}>{b.waiter_name||'—'}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{b.guest_name||'Walk-in'}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)', textDecoration:parseFloat(b.discount_pct||0)>0?'line-through':undefined }}>{fmtKES(b.original_total||b.total)}</td>
                        <td style={{ padding:'8px 9px', color: parseFloat(b.discount_pct||0)>0?'#e67e22':'#555' }}>
                          {parseFloat(b.discount_pct||0)>0 ? `${b.discount_pct}%` : '—'}
                        </td>
                        <td style={{ padding:'8px 9px', fontWeight:700, color:'#c8a96e' }}>{fmtKES(b.total)}</td>
                        <td style={{ padding:'8px 9px', fontSize:11 }}>
                          {b.payment_mode
                            ? <span style={{ color:MODE_COLORS[b.payment_mode]||'#aaa' }}>{MODE_ICONS[b.payment_mode]} {b.payment_mode}</span>
                            : <span style={{ color:'var(--textFaint)' }}>—</span>}
                        </td>
                        <td style={{ padding:'8px 9px' }}>
                          <span style={{ padding:'2px 7px', borderRadius:2, fontSize:9, fontWeight:700, textTransform:'uppercase',
                            background:STATUS_COLORS[b.status]+'22'||'var(--bgInput)', color:STATUS_COLORS[b.status]||'#aaa' }}>
                            {b.is_refunded?'REFUNDED':b.status}
                          </span>
                        </td>
                        <td style={{ padding:'8px 9px', fontSize:11, color: b.receipt_printed?'#2ecc71':'#555' }}>
                          {b.receipt_printed?'✓':'-'}
                        </td>
                        <td style={{ padding:'8px 9px' }}>
                          <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                            <button style={{ ...btnSm, fontSize:10 }} onClick={()=>printBillReceipt(b)}>🖨</button>
                            {b.status==='paid' && !b.is_refunded && (
                              <button style={{ ...btnSm, fontSize:10, color:'#e67e22', background:'var(--bgWarning)' }}
                                onClick={()=>{ setDiscModal(b); setDiscPct(b.discount_pct||''); setDiscReason(''); }}>Disc</button>
                            )}
                            {b.status==='paid' && !b.is_refunded && (
                              <button style={{ ...btnSm, fontSize:10, color:'#e74c3c', background:'#1a0a0a' }}
                                onClick={()=>{ setRefundModal(b); setRefundAmount(String(b.total)); setRefundReason(''); }}>Refund</button>
                            )}
                            {b.status==='paid' && (
                              <button className="btn btn-default btn-sm"
                                onClick={()=>reopenBill(b)}>Reopen</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ══════════════════════════════ MENU ════════════════════════════════════ */}
      {tab==='menu' && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ display:'flex', gap:6 }}>
              {['food','bar'].map(t=>(
                <button key={t} onClick={()=>{ setMenuTab(t); setMenuSearch(''); }}
                  style={{ fontSize:11, padding:'6px 14px', borderRadius:2, fontFamily:'inherit', cursor:'pointer', textTransform:'uppercase', letterSpacing:1,
                    border:`1px solid ${menuTab===t?'#c8a96e':'var(--borderMid)'}`, background:menuTab===t?'#c8a96e':'transparent', color:menuTab===t?'#111':'#666' }}>
                  {t==='food'?'🍽 Food':'🍺 Bar'}
                </button>
              ))}
            </div>
            <input style={{ ...inputStyle, flex:1, minWidth:160 }} placeholder="🔍 Search menu…" value={menuSearch} onChange={e=>setMenuSearch(e.target.value)}/>
            <button style={{ ...btnSm, padding:'7px 14px', whiteSpace:'nowrap', color:'#c8a96e', border:'1px solid #c8a96e44' }} onClick={()=>openCatModal()}>+ Category</button>
            <button style={{ ...btnSm, background:'#c8a96e', color:'#111', padding:'7px 16px', fontWeight:700, whiteSpace:'nowrap' }} onClick={()=>openMenuModal()}>+ Add Item</button>
          </div>

          {/* ── Categories quick-manage strip ─────────────────────────────── */}
          {menuCategories.filter(c => c.menu_type === menuTab).length > 0 && (
            <div style={{ marginBottom:12, padding:'10px 12px', background:'#0d0d0d', borderRadius:5, border:'1px solid #1e1e1e' }}>
              <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>
                {menuTab === 'food' ? '🍽' : '🍺'} {menuTab === 'food' ? 'Food' : 'Bar'} Categories — click to filter, Edit/✕ to manage
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {menuCategories.filter(c => c.menu_type === menuTab).map(cat => (
                  <div key={cat.id} style={{ display:'flex', alignItems:'center', gap:0, borderRadius:4, overflow:'hidden', border:`1px solid ${menuSearch===cat.name?'#c8a96e':'#2a2a2a'}` }}>
                    <button
                      onClick={() => setMenuSearch(s => s === cat.name ? '' : cat.name)}
                      style={{ padding:'4px 10px', background: menuSearch===cat.name ? '#c8a96e22' : '#161616', color: menuSearch===cat.name ? '#c8a96e' : 'var(--textMuted)', fontSize:12, border:'none', cursor:'pointer', fontFamily:'inherit' }}
                    >
                      {cat.name}
                      <span style={{ marginLeft:5, fontSize:10, color:'#555' }}>
                        ({menuItems.filter(i => i.category===cat.name && i.menu_type===menuTab).length})
                      </span>
                    </button>
                    <button onClick={() => openCatModal(cat)} style={{ padding:'4px 6px', background:'#1a1a1a', color:'#888', fontSize:10, border:'none', borderLeft:'1px solid #2a2a2a', cursor:'pointer' }} title="Edit">✏️</button>
                    <button onClick={() => deleteCat(cat)} style={{ padding:'4px 6px', background:'#1a1a1a', color:'#555', fontSize:10, border:'none', borderLeft:'1px solid #2a2a2a', cursor:'pointer' }} title="Delete">✕</button>
                  </div>
                ))}
                <button onClick={() => openCatModal()} style={{ padding:'4px 12px', background:'transparent', color:'#c8a96e', fontSize:12, border:'1px dashed #c8a96e44', borderRadius:4, cursor:'pointer', fontFamily:'inherit' }}>+ Add</button>
              </div>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
            {filteredMenu.length===0
              ? <div style={{gridColumn:'1/-1'}}><Empty text="No menu items found"/></div>
              : filteredMenu.map(item=>(
              <div key={item.id} style={{ background:'var(--bgCard)', border:`1px solid ${item.is_available===false?'var(--bgInput)':'var(--border)'}`, borderRadius:4, padding:14, opacity:item.is_available===false?0.6:1 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)' }}>{item.name}</div>
                    <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>{item.category}</div>
                  </div>
                  <div style={{ display:'flex', gap:4 }}>
                    <button style={{ ...btnSm, fontSize:10 }} onClick={()=>openMenuModal(item)}>Edit</button>
                    <button style={{ ...btnSm, fontSize:10, color:'#e74c3c', background:'#1a0a0a' }} onClick={()=>deleteMenuItem(item.id)}>Del</button>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:6, marginBottom:8 }}>
                  <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'7px 10px' }}>
                    <div style={{ fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>Selling Price</div>
                    <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e' }}>{fmtKES(item.price)}</div>
                  </div>
                  <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'7px 10px' }}>
                    <div style={{ fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>Margin</div>
                    <div style={{ fontSize:15, fontWeight:700, color:margin(item)!==null?(margin(item)<30?'#e74c3c':'#2ecc71'):'#555' }}>
                      {margin(item)!==null?`${margin(item)}%`:'—'}
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontSize:11, color:'var(--textFaint)' }}>
                    {item.cost_price ? `Cost: ${fmtKES(item.cost_price)}` : 'No cost set'}
                  </div>
                  <button
                    style={{ ...btnSm, fontSize:10, color:item.is_available===false?'#2ecc71':'#e67e22', background:item.is_available===false?'#0a180a':'#1a1000' }}
                    onClick={()=>toggleAvailability(item)}>
                    {item.is_available===false?'Activate':'Deactivate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ══════════════════════════════ STAFF ═══════════════════════════════════ */}
      {tab==='staff' && (
        <Card title={`Waiter Performance — ${reports?.period?.label||period}`}>
          {!reports?.waiter_stats?.length ? <Empty text="No waiter data for period"/> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>{['Rank','Waiter','Orders','Revenue','Avg Order','Voids','Discounts'].map(h=>(
                    <th key={h} style={{ background:'#0e0e0e', padding:'8px 10px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {reports.waiter_stats.map((w,i)=>(
                    <tr key={w.id} style={{ borderBottom:'1px solid #1a1a1a' }}>
                      <td style={{ padding:'10px', color:i<3?'#c8a96e':'#555', fontWeight:700, fontSize:16 }}>
                        {i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}
                      </td>
                      <td style={{ padding:'10px', color:'var(--textPrimary)', fontWeight:600 }}>{w.name}</td>
                      <td style={{ padding:'10px', color:'var(--textMuted)' }}>{w.orders}</td>
                      <td style={{ padding:'10px', fontWeight:700, color:'#c8a96e' }}>{fmtKES(w.revenue)}</td>
                      <td style={{ padding:'10px', color:'var(--textMuted)' }}>{fmtKES(w.avg_order)}</td>
                      <td style={{ padding:'10px', color:w.void_count>0?'#e74c3c':'#555' }}>{w.void_count}</td>
                      <td style={{ padding:'10px', color:w.discount_bills>0?'#e67e22':'#555' }}>{w.discount_bills}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bar chart */}
          {reports?.waiter_stats?.length > 0 && (
            <div style={{ marginTop:20 }}>
              <div style={{ fontSize:11, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Revenue by Waiter</div>
              <BarChart
                data={reports.waiter_stats}
                labelKey="name" valueKey="revenue"
                height={110} fmtVal={v=>fmtKES(v)}
              />
            </div>
          )}
        </Card>
      )}

      {/* ══════════════════════════════ ANALYTICS ═══════════════════════════════ */}
      {tab==='analytics' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
          {/* Revenue trend */}
          <Card title="Daily Revenue Trend">
            {reports?.daily_trend?.length ? (
              <>
                <Sparkline data={reports.daily_trend} valueKey="revenue" height={60}/>
                <div style={{ overflowX:'auto', maxHeight:200 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                    <thead><tr>
                      <th style={{ padding:'5px 8px', textAlign:'left', color:'var(--textFaint)', fontWeight:400, fontSize:9, textTransform:'uppercase' }}>Date</th>
                      <th style={{ padding:'5px 8px', textAlign:'right', color:'var(--textFaint)', fontWeight:400, fontSize:9, textTransform:'uppercase' }}>Revenue</th>
                      <th style={{ padding:'5px 8px', textAlign:'right', color:'var(--textFaint)', fontWeight:400, fontSize:9, textTransform:'uppercase' }}>Orders</th>
                    </tr></thead>
                    <tbody>
                      {[...reports.daily_trend].reverse().map(d=>(
                        <tr key={d.date} style={{ borderBottom:'1px solid #111' }}>
                          <td style={{ padding:'5px 8px', color:'var(--textMuted)' }}>{d.date}</td>
                          <td style={{ padding:'5px 8px', textAlign:'right', color:'#c8a96e', fontWeight:600 }}>{fmtKES(d.revenue)}</td>
                          <td style={{ padding:'5px 8px', textAlign:'right', color:'var(--textMuted)' }}>{d.orders}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <Empty text="No trend data"/>}
          </Card>

          {/* Item performance */}
          <Card title="Item Performance (Top 20)">
            {reports?.item_sales?.length ? (
              <div style={{ overflowX:'auto', maxHeight:320 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                  <thead><tr>
                    {['Item','Qty Sold','Revenue'].map(h=>(
                      <th key={h} style={{ padding:'5px 8px', textAlign:'left', color:'var(--textFaint)', fontWeight:400, fontSize:9, textTransform:'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {reports.item_sales.map((it,i)=>(
                      <tr key={i} style={{ borderBottom:'1px solid #111' }}>
                        <td style={{ padding:'5px 8px', color:'var(--textMuted)' }}>{it.name}</td>
                        <td style={{ padding:'5px 8px', color:'var(--textMuted)' }}>×{it.qty}</td>
                        <td style={{ padding:'5px 8px', color:'#c8a96e', fontWeight:600 }}>{fmtKES(it.revenue||0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty text="No item data"/>}
          </Card>

          {/* Payment method breakdown */}
          <Card title="Payment Method Breakdown">
            {reports?.payment_methods && Object.entries(reports.payment_methods).map(([mode,amt])=>{
              const pct = rev?.total>0?Math.round(amt/rev.total*100):0;
              return (
                <div key={mode} style={{ marginBottom:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3 }}>
                    <span style={{ color:'var(--textMuted)' }}>{MODE_ICONS[mode]||'💰'} {mode.replace('-',' ').toUpperCase()}</span>
                    <span style={{ color:MODE_COLORS[mode]||'#c8a96e', fontWeight:700 }}>{fmtKES(amt)}</span>
                  </div>
                  <div style={{ height:4, background:'var(--bgInput)', borderRadius:2 }}>
                    <div style={{ height:4, background:MODE_COLORS[mode]||'#c8a96e', borderRadius:2, width:`${pct}%`, transition:'width .3s' }}/>
                  </div>
                  <div style={{ fontSize:10, color:'var(--textFaint)', marginTop:1 }}>{pct}% of revenue</div>
                </div>
              );
            })}
          </Card>

          {/* Category breakdown */}
          <Card title="Category Sales">
            {reports?.category_sales?.length ? (
              <BarChart
                data={reports.category_sales.slice(0,12)}
                labelKey="item__category" valueKey="revenue"
                height={120} fmtVal={v=>fmtKES(v)}
              />
            ) : <Empty text="No category data"/>}
          </Card>
        </div>
      )}

      {/* ══════════════════════════════ RETURNS ══════════════════════════════════ */}
      {tab==='returns' && (
        <>
          {/* Summary cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:8, marginBottom:12 }}>
            {[
              ['Total Returns', returns.length,           '#3498db'],
              ['Pending Review', pendingReturns.length,   pendingReturns.length>0?'#e74c3c':'#555'],
              ['Approved',       approvedReturns.length,  '#2ecc71'],
              ['Value Approved', fmtKES(totalReturnValue),'#c8a96e'],
            ].map(([l,v,c])=>(
              <div key={l} style={{ background:'var(--bgCard)', border:'1px solid #1e1e1e', borderRadius:4, padding:'12px 14px' }}>
                <div style={{ fontSize:10, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:20, fontWeight:700, color:c }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Pending banner */}
          {pendingReturns.length > 0 && (
            <div style={{ padding:'10px 14px', background:'#1a0a0a', border:'1px solid #e74c3c44', borderRadius:4, marginBottom:12, fontSize:12 }}>
              <span style={{ color:'#e74c3c', fontWeight:700 }}>⚠ {pendingReturns.length} return{pendingReturns.length!==1?'s':''} awaiting your review — </span>
              <span style={{ color:'var(--textMuted)' }}>{pendingReturns.map(r=>r.ref).join(', ')}</span>
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:10 }}>
            <button style={{ ...btnSm, background:'#c8a96e', color:'#111', fontWeight:700, padding:'8px 18px' }}
              onClick={()=>{ setReturnForm({ source:'restaurant', reason:'other', item_name:'', quantity:'1', unit_price:'', customer_name:'', description:'' }); setReturnModal(true); }}>
              + Log New Return
            </button>
          </div>

          <Card title="All Customer Returns">
            {returns.length===0 ? <Empty text="No returns logged yet"/> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>{['Ref','Source','Item','Qty','Value','Reason','Customer','Status','Logged By','Actions'].map(h=>(
                      <th key={h} style={{ background:'#0e0e0e', padding:'7px 9px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {returns.map(r=>(
                      <tr key={r.id} style={{ borderBottom:'1px solid #111' }}>
                        <td style={{ padding:'8px 9px', color:'#c8a96e', fontWeight:700, fontFamily:'monospace', fontSize:11 }}>{r.ref}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)', fontSize:11, textTransform:'capitalize' }}>{r.source_label||r.source}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textPrimary)', fontWeight:600 }}>{r.item_name}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>×{parseFloat(r.quantity)}</td>
                        <td style={{ padding:'8px 9px', color:'#c8a96e', fontWeight:700 }}>{fmtKES(r.total_value)}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)', fontSize:11 }}>{r.reason_label||r.reason}</td>
                        <td style={{ padding:'8px 9px', color:'var(--textMuted)' }}>{r.customer_name||'—'}</td>
                        <td style={{ padding:'8px 9px' }}>
                          <span style={{ padding:'2px 7px', borderRadius:2, fontSize:9, fontWeight:700, textTransform:'uppercase',
                            background: r.status==='approved'?'#2ecc7122':r.status==='rejected'?'#e74c3c22':'#e67e2222',
                            color:      r.status==='approved'?'#2ecc71' :r.status==='rejected'?'#e74c3c' :'#e67e22' }}>
                            {r.status_label||r.status}
                          </span>
                        </td>
                        <td style={{ padding:'8px 9px', color:'var(--textFaint)', fontSize:11 }}>{r.created_by_name}</td>
                        <td style={{ padding:'8px 9px' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            <button style={{ ...btnSm, fontSize:10 }} onClick={()=>setReturnDetail(r)}>View</button>
                            {r.status === 'pending' && (
                              <>
                                <button style={{ ...btnSm, fontSize:10, color:'#2ecc71', background:'#0a180a' }}
                                  onClick={()=>{ setActionModal(r); setActionType('approve'); setActionResolution(''); }}>✓ Approve</button>
                                <button style={{ ...btnSm, fontSize:10, color:'#e74c3c', background:'#1a0a0a' }}
                                  onClick={()=>{ setActionModal(r); setActionType('reject'); setActionResolution(''); }}>✕ Reject</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ══════════════════════════════ ACTIVITY ════════════════════════════════ */}
      {/* ══════════════════════════ SHOPPING LISTS ════════════════════════════ */}
      {tab==='shopping' && (
        <div>
          <Card title="🛒 Shopping List Approvals">
            <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:14 }}>
              Shopping lists auto-generated by the storekeeper from low-stock items require your approval before they can be printed and purchased.
            </div>
            {shoppingLists.length === 0 ? <Empty text="No shopping lists submitted yet" /> : (
              <div style={{ display:'grid', gap:10 }}>
                {shoppingLists.map(sl => {
                  const statusColor = { pending:'#c8a96e', approved:'#2ecc71', rejected:'#e74c3c', purchased:'#3498db' }[sl.status] || '#888';
                  return (
                    <div key={sl.id} style={{ background:'var(--bgCard)', border:`1px solid ${statusColor}33`, borderLeft:`4px solid ${statusColor}`, borderRadius:4, padding:14 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                        <div>
                          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                            <span style={{ fontSize:14, fontWeight:700, color:'var(--textPrimary)' }}>{sl.title}</span>
                            <span style={{ fontSize:10, padding:'2px 8px', borderRadius:3, fontWeight:700, textTransform:'uppercase', background:`${statusColor}22`, color:statusColor }}>{sl.status}</span>
                            <span style={{ fontSize:11, color:'var(--textMuted)' }}>{sl.ref}</span>
                          </div>
                          <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:4 }}>
                            Created by <strong style={{ color:'var(--textMuted)' }}>{sl.created_by_name}</strong> · {new Date(sl.created_at).toLocaleString('en-KE')}
                          </div>
                          {sl.notes && <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:2, fontStyle:'italic' }}>{sl.notes}</div>}
                          {sl.rejection_reason && <div style={{ fontSize:11, color:'#e74c3c', marginTop:4 }}>Rejected: {sl.rejection_reason}</div>}
                          {sl.approved_by_name && <div style={{ fontSize:11, color:'#2ecc71', marginTop:2 }}>Approved by {sl.approved_by_name} · {sl.approved_at ? new Date(sl.approved_at).toLocaleString('en-KE') : ''}</div>}
                        </div>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                          {/* Approval stamps — displayed once manager has approved */}
                          {['manager_approved','accountant_approved','purchased'].includes(sl.status) && (
                            <div style={{
                              display:'flex', gap:6, alignItems:'flex-end',
                              padding:'6px 10px',
                              background:'rgba(200,169,110,0.05)',
                              border:'1px solid rgba(200,169,110,0.12)',
                              borderRadius:6,
                            }}>
                              <ManagerStamp
                                by={sl.approved_by_name && sl.approved_by_name !== '—' ? sl.approved_by_name : null}
                                at={sl.approved_at}
                                size={54}
                              />
                              {(sl.status === 'accountant_approved' || sl.status === 'purchased') && (
                                <AccountantStampMini
                                  by={sl.accountant_approved_by_name && sl.accountant_approved_by_name !== '—' ? sl.accountant_approved_by_name : null}
                                  at={sl.accountant_approved_at}
                                  size={54}
                                />
                              )}
                            </div>
                          )}
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:11, color:'var(--textFaint)' }}>Est. Total</div>
                            <div style={{ fontSize:16, fontWeight:700, color:'#c8a96e' }}>KES {parseFloat(sl.total_estimated||0).toLocaleString('en-KE')}</div>
                            <div style={{ fontSize:10, color:'var(--textFaint)' }}>{(sl.items||[]).length} items</div>
                          </div>
                          <button style={{ ...btnSm, fontSize:11 }} onClick={()=>setSlDetail(slDetail?.id===sl.id?null:sl)}>
                            {slDetail?.id===sl.id ? '▲ Hide' : '▼ View'}
                          </button>
                          {sl.status==='pending' && (
                            <>
                              <button
                                style={{ ...btnSm, fontSize:11, background:'#0a1a0a', color:'#2ecc71', border:'1px solid #2ecc7155', padding:'7px 14px', fontWeight:700 }}
                                onClick={async()=>{
                                  try { await api.approveShoppingList(sl.id); await load(); notify(`Shopping list "${sl.title}" approved!`); }
                                  catch(e) { notify(e.response?.data?.error||'Error approving','error'); }
                                }}>
                                ✓ Approve
                              </button>
                              <button
                                style={{ ...btnSm, fontSize:11, background:'#1a0a0a', color:'#e74c3c', border:'1px solid #e74c3c44', padding:'7px 14px' }}
                                onClick={()=>{ setSlRejectModal(sl); setSlRejectReason(''); }}>
                                ✕ Reject
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Items detail */}
                      {slDetail?.id===sl.id && (
                        <div style={{ marginTop:14, borderTop:'1px solid #1e1e1e', paddingTop:14 }}>
                          <div style={{ fontSize:10, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Items</div>
                          <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                              <thead>
                                <tr>{['Item','Current Stock','Reorder Level','Qty to Buy','Unit','Est. Cost','Notes'].map(h=>(
                                  <th key={h} style={{ background:'#0e0e0e', padding:'6px 8px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                                ))}</tr>
                              </thead>
                              <tbody>
                                {(sl.items||[]).map((item,i)=>(
                                  <tr key={i} style={{ borderBottom:'1px solid #1a1a1a' }}>
                                    <td style={{ padding:'7px 8px', color:'var(--textPrimary)', fontWeight:600 }}>{item.item_name_auto||item.item_name}</td>
                                    <td style={{ padding:'7px 8px', color:parseFloat(item.current_stock||0)<=parseFloat(item.reorder_level||0)?'#e74c3c':'#aaa' }}>{item.current_stock||'—'}</td>
                                    <td style={{ padding:'7px 8px', color:'var(--textFaint)' }}>{item.reorder_level||'—'}</td>
                                    <td style={{ padding:'7px 8px', color:'#c8a96e', fontWeight:700 }}>{item.qty_required}</td>
                                    <td style={{ padding:'7px 8px', color:'var(--textFaint)' }}>{item.unit}</td>
                                    <td style={{ padding:'7px 8px', color:'#c8a96e' }}>{item.estimated_cost ? `KES ${parseFloat(item.estimated_cost).toLocaleString('en-KE')}` : '—'}</td>
                                    <td style={{ padding:'7px 8px', color:'var(--textFaint)', fontSize:11 }}>{item.notes||'—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8, fontSize:13, fontWeight:700, color:'#c8a96e' }}>
                            Estimated Total: KES {parseFloat(sl.total_estimated||0).toLocaleString('en-KE')}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Reject modal */}
          {slRejectModal && (
            <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
              <div style={{ background:'var(--bgCard)',border:'1px solid #2a2a2a',borderRadius:6,padding:24,maxWidth:480,width:'100%', overflowX:'hidden' }}>
                <div style={{ fontSize:15,fontWeight:700,color:'#e74c3c',marginBottom:4 }}>✕ Reject Shopping List</div>
                <div style={{ fontSize:13,color:'var(--textMuted)',marginBottom:16 }}>{slRejectModal.title}</div>
                <Field label="Reason for rejection *">
                  <textarea style={{ ...inputStyle,minHeight:80,resize:'vertical' }} placeholder="Explain why this list is being rejected…" value={slRejectReason} onChange={e=>setSlRejectReason(e.target.value)}/>
                </Field>
                <div style={{ display:'flex',gap:8,marginTop:14 }}>
                  <button
                    style={{ ...btnPrimary,marginTop:0,flex:1,background:'#1a0a0a',border:'1px solid #e74c3c44',color:'#e74c3c' }}
                    onClick={async()=>{
                      if (!slRejectReason.trim()) { notify('Please provide a rejection reason','error'); return; }
                      try {
                        await api.rejectShoppingList(slRejectModal.id,{ rejection_reason:slRejectReason });
                        setSlRejectModal(null); setSlRejectReason('');
                        await load(); notify('Shopping list rejected');
                      } catch(e) { notify('Error rejecting','error'); }
                    }}>
                    Confirm Reject
                  </button>
                  <button style={{ ...btnSm,padding:'11px 20px' }} onClick={()=>setSlRejectModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════ COMPLIMENTARY BILLS ═════════════════════ */}
      {tab==='complimentary' && (() => {
        const SCOLOR = { pending:'#f59e0b', approved:'#22c55e', rejected:'#ef4444' };
        const SBG    = { pending:'rgba(245,158,11,0.12)', approved:'rgba(34,197,94,0.1)', rejected:'rgba(239,68,68,0.1)' };
        const CAT_LABEL = { vip_guest:'VIP Guest', complaint:'Complaint Resolution', staff_meal:'Staff Meal', marketing:'Marketing / Promotion', error:'Billing Error', other:'Other' };
        const filtered = compFilter === 'all' ? compBills : compBills.filter(c => c.status === compFilter);
        const pending  = compBills.filter(c => c.status === 'pending').length;
        const approved = compBills.filter(c => c.status === 'approved').length;
        const totalExp = compBills.filter(c => c.status === 'approved').reduce((s,c) => s + parseFloat(c.amount||0), 0);

        const doApprove = async (c) => {
          try {
            await api.approveComplimentaryBill(c.id);
            notify('✅ Complimentary bill approved — expense recorded');
            setCompSelected(null); load();
          } catch(e) { notify(e.response?.data?.error || 'Approval failed', 'error'); }
        };

        const doReject = async () => {
          if (!compRejectModal) return;
          try {
            await api.rejectComplimentaryBill(compRejectModal.id, { reason: compRejectReason });
            notify('Complimentary bill rejected');
            setCompRejectModal(null); setCompRejectReason(''); setCompSelected(null); load();
          } catch(e) { notify(e.response?.data?.error || 'Rejection failed', 'error'); }
        };

        return (
          <div>
            {/* Summary */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:16 }}>
              {[
                { label:'Pending Approval', value:pending,           color:'#f59e0b' },
                { label:'Approved',         value:approved,          color:'#22c55e' },
                { label:'Rejected',         value:compBills.filter(c=>c.status==='rejected').length, color:'#ef4444' },
                { label:'Total Expensed',   value:fmtKES(totalExp),  color:'#c45e3e' },
              ].map(s => (
                <div key={s.label} style={{ background:'var(--bgCard)', border:`1px solid var(--border)`, borderTop:`3px solid ${s.color}`, borderRadius:8, padding:'14px 16px', textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800, color:s.color, marginBottom:4 }}>{s.value}</div>
                  <div style={{ fontSize:11, color:'var(--textMuted)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {pending > 0 && (
              <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8, padding:'10px 16px', marginBottom:14, fontSize:13, color:'#f59e0b', display:'flex', alignItems:'center', gap:8 }}>
                ⏳ <strong>{pending} complimentary bill{pending!==1?'s':''}</strong> awaiting your approval
              </div>
            )}

            {/* Filter bar */}
            <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
              {['all','pending','approved','rejected'].map(f => (
                <button key={f} onClick={()=>setCompFilter(f)} style={{
                  padding:'6px 14px', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer',
                  border:`1px solid ${compFilter===f?'var(--primary)':'var(--border)'}`,
                  background:compFilter===f?'var(--primary)':'var(--bgInput)',
                  color:compFilter===f?'#fff':'var(--textMuted)',
                }}>
                  {f.charAt(0).toUpperCase()+f.slice(1)} ({f==='all'?compBills.length:compBills.filter(c=>c.status===f).length})
                </button>
              ))}
            </div>

            {/* Split layout: list + detail */}
            <div style={{ display:'grid', gridTemplateColumns:compSelected?'1fr 360px':'1fr', gap:14 }} className="layout-sidebar">
              <Card title={`Complimentary Bills (${filtered.length})`}>
                {filtered.length === 0 ? <Empty text="No records match this filter" /> : (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead>
                        <tr style={{ borderBottom:'2px solid var(--border)' }}>
                          {['Ref','Guest','Category','Amount','By','Date','Status'].map(h=>(
                            <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--textMuted)', textTransform:'uppercase', letterSpacing:0.5, whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((c,i) => (
                          <tr key={c.id} onClick={()=>setCompSelected(compSelected?.id===c.id?null:c)}
                            style={{ borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background .15s',
                              background:compSelected?.id===c.id?'var(--bgHover)': i%2===0?'transparent':'rgba(0,0,0,0.015)' }}>
                            <td style={{ padding:'9px 10px', fontFamily:'monospace', fontSize:11, color:'#c45e3e', fontWeight:700 }}>{c.ref}</td>
                            <td style={{ padding:'9px 10px', fontWeight:600, color:'var(--textPrimary)', whiteSpace:'nowrap' }}>{c.guest_name}</td>
                            <td style={{ padding:'9px 10px', color:'var(--textMuted)', fontSize:11, textTransform:'capitalize', whiteSpace:'nowrap' }}>{CAT_LABEL[c.category]||c.category}</td>
                            <td style={{ padding:'9px 10px', fontWeight:700, color:'var(--textPrimary)', whiteSpace:'nowrap' }}>{fmtKES(c.amount)}</td>
                            <td style={{ padding:'9px 10px', color:'var(--textMuted)', fontSize:11, whiteSpace:'nowrap' }}>{c.requested_by_name||'—'}</td>
                            <td style={{ padding:'9px 10px', color:'var(--textFaint)', fontSize:11, whiteSpace:'nowrap' }}>{c.created_at?new Date(c.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short'}):''}</td>
                            <td style={{ padding:'9px 10px' }}>
                              <span style={{ fontSize:10, fontWeight:700, borderRadius:4, padding:'3px 8px', background:SBG[c.status]||'rgba(0,0,0,0.05)', color:SCOLOR[c.status]||'#888', textTransform:'capitalize', whiteSpace:'nowrap' }}>{c.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Detail / action panel */}
              {compSelected && (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  <Card title={`Bill ${compSelected.ref}`}>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {[
                        ['Guest',      compSelected.guest_name],
                        ['Category',   CAT_LABEL[compSelected.category]||compSelected.category],
                        ['Amount',     fmtKES(compSelected.amount)],
                        ['Requested by', compSelected.requested_by_name||'—'],
                        ['Submitted',  compSelected.created_at ? new Date(compSelected.created_at).toLocaleString('en-GB') : '—'],
                        compSelected.approved_by_name ? ['Actioned by', compSelected.approved_by_name] : null,
                        compSelected.approved_at ? ['Actioned at', new Date(compSelected.approved_at).toLocaleString('en-GB')] : null,
                        compSelected.rejection_reason ? ['Rejection reason', compSelected.rejection_reason] : null,
                      ].filter(Boolean).map(([k,v])=>(
                        <div key={k} style={{ display:'flex', justifyContent:'space-between', gap:8, borderBottom:'1px solid var(--border)', paddingBottom:8 }}>
                          <span style={{ fontSize:11, color:'var(--textMuted)', fontWeight:600, flexShrink:0 }}>{k}</span>
                          <span style={{ fontSize:12, color:'var(--textPrimary)', textAlign:'right', wordBreak:'break-word' }}>{v}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginTop:14, padding:'10px 12px', background:'var(--bgInput)', borderRadius:6, border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:10, color:'var(--textMuted)', marginBottom:4, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>Reason / Justification</div>
                      <div style={{ fontSize:12, color:'var(--textSecond)', lineHeight:1.6 }}>{compSelected.reason}</div>
                    </div>

                    {/* Status badge */}
                    <div style={{ textAlign:'center', margin:'14px 0 10px' }}>
                      <span style={{ fontSize:12, fontWeight:700, borderRadius:6, padding:'5px 16px', background:SBG[compSelected.status], color:SCOLOR[compSelected.status], textTransform:'uppercase', letterSpacing:1 }}>{compSelected.status}</span>
                    </div>

                    {/* Manager approval stamp — shown when approved */}
                    {compSelected.status === 'approved' && (
                      <div style={{
                        display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap',
                        padding:'12px 14px', marginBottom:10,
                        background:'rgba(200,169,110,0.05)',
                        border:'1px solid rgba(200,169,110,0.15)', borderRadius:6,
                      }}>
                        <div style={{ width:'100%', fontSize:10, color:'#666', letterSpacing:2, textTransform:'uppercase', marginBottom:2 }}>
                          Official Approval Stamp
                        </div>
                        <ManagerStamp
                          by={compSelected.approved_by_name && compSelected.approved_by_name !== '—' ? compSelected.approved_by_name : null}
                          at={compSelected.approved_at}
                          size={76}
                        />
                      </div>
                    )}

                    {/* Action buttons — only show for pending */}
                    {compSelected.status === 'pending' && (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8 }}>
                        <button
                          onClick={() => doApprove(compSelected)}
                          style={{ padding:'10px', borderRadius:6, border:'2px solid #22c55e', background:'rgba(34,197,94,0.1)', color:'#22c55e', fontWeight:700, cursor:'pointer', fontSize:13 }}>
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => { setCompRejectModal(compSelected); setCompRejectReason(''); }}
                          style={{ padding:'10px', borderRadius:6, border:'2px solid #ef4444', background:'rgba(239,68,68,0.08)', color:'#ef4444', fontWeight:700, cursor:'pointer', fontSize:13 }}>
                          ✕ Reject
                        </button>
                      </div>
                    )}
                    <button onClick={() => printComplimentaryAuthorization(compSelected)} style={{ marginTop:8, width:'100%', padding:'8px', background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:6, color:'var(--textMuted)', cursor:'pointer', fontSize:12 }}>
                      🖨️ Print Authorization
                    </button>
                    <button onClick={()=>setCompSelected(null)} style={{ marginTop:6, width:'100%', padding:'8px', background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:6, color:'var(--textMuted)', cursor:'pointer', fontSize:12 }}>
                      ← Back to list
                    </button>
                  </Card>
                </div>
              )}
            </div>

            {/* Reject modal */}
            {compRejectModal && (
              <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
                <div className="modal-inner" style={{ background:'var(--bgCard)', border:'2px solid #ef444444', borderRadius:10, padding:24, maxWidth:440, width:'100%' }}>
                  <div style={{ fontSize:16, fontWeight:700, color:'#ef4444', marginBottom:6 }}>✕ Reject Complimentary Bill</div>
                  <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:16 }}>
                    Rejecting <strong>{compRejectModal.ref}</strong> — {compRejectModal.guest_name} — {fmtKES(compRejectModal.amount)}
                  </div>
                  <div style={{ fontSize:12, color:'var(--textMuted)', marginBottom:6 }}>Rejection reason (optional)</div>
                  <textarea
                    style={{ width:'100%', padding:'10px 12px', background:'var(--bgInput)', border:'1px solid var(--border)', borderRadius:6, color:'var(--textPrimary)', fontSize:13, resize:'vertical', minHeight:80, boxSizing:'border-box' }}
                    value={compRejectReason} onChange={e=>setCompRejectReason(e.target.value)}
                    placeholder="Explain why this complimentary is being rejected…"
                  />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:14 }}>
                    <button onClick={()=>{ setCompRejectModal(null); setCompRejectReason(''); }} style={{ padding:'10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bgInput)', color:'var(--textMuted)', cursor:'pointer', fontWeight:600 }}>Cancel</button>
                    <button onClick={doReject} style={{ padding:'10px', borderRadius:6, border:'none', background:'#ef4444', color:'#fff', cursor:'pointer', fontWeight:700 }}>Confirm Reject</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════════════════════════ FOOD COSTING ══════════════════════════════ */}
      {tab==='costing' && <FoodCostingCalculator notify={notify} />}

      {/* ══════════════════════════ ACTIVITY LOG ══════════════════════════════ */}
      {tab==='activity' && (
        <Card title="Restaurant Activity Log (Last 200 events)">
      {tab==='donations' && <DonationPanel notify={notify} />}
          {auditLogs.length===0 ? <Empty text="No activity logged"/> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>{['Time','User','Role','Action','Details'].map(h=>(
                    <th key={h} style={{ background:'#0e0e0e', padding:'7px 9px', textAlign:'left', fontSize:9, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {auditLogs.map(log=>(
                    <tr key={log.id} style={{ borderBottom:'1px solid #111' }}>
                      <td style={{ padding:'7px 9px', color:'var(--textFaint)', fontSize:11, whiteSpace:'nowrap' }}>{new Date(log.created_at).toLocaleString('en-KE')}</td>
                      <td style={{ padding:'7px 9px', color:'#3498db', fontWeight:600 }}>{log.user_name||'System'}</td>
                      <td style={{ padding:'7px 9px', color:'var(--textMuted)', fontSize:10 }}>{log.user_role||'—'}</td>
                      <td style={{ padding:'7px 9px' }}>
                        <span style={{ padding:'2px 7px', borderRadius:2, fontSize:9, fontWeight:700, textTransform:'uppercase', background:'var(--bgInput)', color: log.action.includes('void')||log.action.includes('refund')?'#e74c3c':log.action.includes('discount')?'#e67e22':log.action.includes('paid')?'#2ecc71':'#c8a96e' }}>
                          {log.action_label||log.action}
                        </span>
                      </td>
                      <td style={{ padding:'7px 9px', color:'var(--textMuted)' }}>{log.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ═══════════════════════════ MODALS ══════════════════════════════════════ */}
      {/* Menu item modal */}
      {menuModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:520, width:'100%', overflowX:'hidden', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e', marginBottom:16 }}>{editMenu?'Edit Menu Item':'Add Menu Item'}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Item Name *"><input style={inputStyle} value={menuForm.name} onChange={e=>setMenuForm(f=>({...f,name:e.target.value}))}/></Field>
              </div>
              <Field label="Menu Type">
                <select style={inputStyle} value={menuForm.menu_type} onChange={e=>setMenuForm(f=>({...f,menu_type:e.target.value,category:''}))}>
                  <option value="food">🍽 Food</option>
                  <option value="bar">🍺 Bar</option>
                </select>
              </Field>
              <Field label="Category *">
                <select style={inputStyle} value={menuForm.category} onChange={e=>setMenuForm(f=>({...f,category:e.target.value}))}>
                  <option value="">— Select Category —</option>
                  {(menuForm.menu_type==='food'?FOOD_CATS:BAR_CATS).map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              {/* Typed price fields with KES preview */}
              <Field label="Selling Price (KES) *">
                <input type="number" min="0" step="1" style={{ ...inputStyle, fontSize:16, fontWeight:700, color:'#c8a96e' }}
                  placeholder="e.g. 850" value={menuForm.price} onChange={e=>setMenuForm(f=>({...f,price:e.target.value}))}/>
                {menuForm.price && <div style={{ fontSize:11, color:'#c8a96e', marginTop:3 }}>= {fmtKES(parseFloat(menuForm.price)||0)}</div>}
              </Field>
              <Field label="Cost Price (KES) — for margins">
                <input type="number" min="0" step="1" style={{ ...inputStyle, fontSize:16, color:'var(--textMuted)' }}
                  placeholder="e.g. 350 (optional)" value={menuForm.cost_price} onChange={e=>setMenuForm(f=>({...f,cost_price:e.target.value}))}/>
                {menuForm.price && menuForm.cost_price && parseFloat(menuForm.price)>0 && (
                  <div style={{ fontSize:11, color:'#2ecc71', marginTop:3 }}>
                    Margin: {Math.round((1-parseFloat(menuForm.cost_price)/parseFloat(menuForm.price))*100)}%
                  </div>
                )}
              </Field>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Description"><textarea style={{ ...inputStyle, minHeight:55, resize:'vertical' }} value={menuForm.description} onChange={e=>setMenuForm(f=>({...f,description:e.target.value}))}/></Field>
              </div>
              <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" id="avail" checked={menuForm.is_available} onChange={e=>setMenuForm(f=>({...f,is_available:e.target.checked}))} style={{ accentColor:'#c8a96e' }}/>
                <label htmlFor="avail" style={{ fontSize:13, color:'var(--textMuted)', cursor:'pointer' }}>Available for ordering</label>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1 }} onClick={saveMenuItem}>{editMenu?'Update Item':'Add to Menu'}</button>
              <button style={{ ...btnSm, padding:'11px 20px' }} onClick={()=>setMenuModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Category management modal ─────────────────────────────────────── */}
      {catModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:210, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:420, width:'100%' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e', marginBottom:16 }}>
              {editCat ? '✏️ Edit Category' : '➕ New Category'}
            </div>
            <Field label="Category Name *">
              <input style={inputStyle} value={catForm.name} autoFocus
                onChange={e => setCatForm(f => ({...f, name: e.target.value}))}
                placeholder="e.g. Grills, Cocktails…"/>
            </Field>
            <Field label="Menu Type">
              <select style={inputStyle} value={catForm.menu_type} onChange={e => setCatForm(f => ({...f, menu_type: e.target.value}))}>
                <option value="food">🍽 Food</option>
                <option value="bar">🍺 Bar</option>
              </select>
            </Field>
            <Field label="Sort Order (lower = appears first)">
              <input type="number" min="0" style={inputStyle} value={catForm.sort_order}
                onChange={e => setCatForm(f => ({...f, sort_order: parseInt(e.target.value)||0}))}/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1 }} onClick={saveCat} disabled={catSaving}>
                {catSaving ? 'Saving…' : editCat ? 'Update Category' : 'Create Category'}
              </button>
              <button style={{ ...btnSm, padding:'11px 20px' }} onClick={() => setCatModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Void order modal */}
      {voidModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #e74c3c44', borderRadius:6, padding:24, maxWidth:420, width:'100%', overflowX:'hidden' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e74c3c', marginBottom:6 }}>⚠ Void Order</div>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:14 }}>
              Order #{voidModal.id} — {voidModal.waiter_name} — Table {voidModal.table_number} — <strong style={{ color:'#c8a96e' }}>{fmtKES(voidModal.total)}</strong>
            </div>
            <Field label="Void Reason * (required for audit log)">
              <textarea style={{ ...inputStyle, minHeight:80, resize:'vertical' }} placeholder="Explain why this order is being voided…"
                value={voidReason} onChange={e=>setVoidReason(e.target.value)} autoFocus/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1, background:'#6b1a1a' }} onClick={voidOrder}>⚠ Confirm Void</button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setVoidModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Discount modal */}
      {discModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #e67e2244', borderRadius:6, padding:24, maxWidth:420, width:'100%', overflowX:'hidden' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e67e22', marginBottom:6 }}>Apply Discount</div>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:14 }}>
              Bill #{discModal.id} — <strong style={{ color:'#c8a96e' }}>{fmtKES(discModal.total)}</strong>
            </div>
            <Field label="Discount % (1–100)">
              <input type="number" min="1" max="100" style={{ ...inputStyle, fontSize:18, fontWeight:700, color:'#e67e22' }}
                placeholder="e.g. 10" value={discPct} onChange={e=>setDiscPct(e.target.value)} autoFocus/>
              {discPct && parseFloat(discPct)>0 && (
                <div style={{ fontSize:12, color:'#2ecc71', marginTop:4 }}>
                  New total: {fmtKES(parseFloat(discModal.total)*(1-parseFloat(discPct)/100))} (save {fmtKES(parseFloat(discModal.total)*parseFloat(discPct)/100)})
                </div>
              )}
            </Field>
            <Field label="Reason * (required)">
              <textarea style={{ ...inputStyle, minHeight:60, resize:'vertical' }} placeholder="e.g. Loyalty customer, complaint resolution…"
                value={discReason} onChange={e=>setDiscReason(e.target.value)}/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1, background:'#6b4a1a' }} onClick={applyDiscount}>Apply Discount</button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setDiscModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Refund modal */}
      {refundModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #e74c3c44', borderRadius:6, padding:24, maxWidth:420, width:'100%', overflowX:'hidden' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e74c3c', marginBottom:6 }}>Process Refund</div>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:14 }}>
              Bill #{refundModal.id} — Paid: <strong style={{ color:'#c8a96e' }}>{fmtKES(refundModal.total)}</strong>
            </div>
            <Field label="Refund Amount (KES)">
              <input type="number" min="0.01" max={refundModal.total} step="0.01" style={{ ...inputStyle, fontSize:18, fontWeight:700, color:'#e74c3c' }}
                placeholder={String(refundModal.total)} value={refundAmount} onChange={e=>setRefundAmount(e.target.value)} autoFocus/>
            </Field>
            <Field label="Reason * (required)">
              <textarea style={{ ...inputStyle, minHeight:60, resize:'vertical' }} placeholder="e.g. Wrong item delivered, quality complaint…"
                value={refundReason} onChange={e=>setRefundReason(e.target.value)}/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1, background:'#6b1a1a' }} onClick={processRefund}>Process Refund</button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setRefundModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Return — log new modal */}
      {returnModal && (
        <div style={{ position:'fixed', inset:0, background:'var(--bgModal)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #c8a96e44', borderRadius:6, padding:24, maxWidth:520, width:'100%', overflowX:'hidden', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#c8a96e', marginBottom:16 }}>↩️ Log New Return / Complaint</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Item Name *">
                  <input style={inputStyle} placeholder="e.g. Grilled Tilapia, Tusker 500ml…" value={returnForm.item_name} onChange={e=>setReturnForm(f=>({...f,item_name:e.target.value}))} autoFocus/>
                </Field>
              </div>
              <Field label="Source">
                <select style={inputStyle} value={returnForm.source} onChange={e=>setReturnForm(f=>({...f,source:e.target.value}))}>
                  {RETURN_SOURCES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Reason">
                <select style={inputStyle} value={returnForm.reason} onChange={e=>setReturnForm(f=>({...f,reason:e.target.value}))}>
                  {RETURN_REASONS.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </Field>
              <Field label="Quantity">
                <input type="number" min="0.01" step="0.01" style={inputStyle} value={returnForm.quantity} onChange={e=>setReturnForm(f=>({...f,quantity:e.target.value}))}/>
              </Field>
              <Field label="Unit Price (KES) *">
                <input type="number" min="0" step="1" style={{ ...inputStyle, fontSize:16, fontWeight:700, color:'#c8a96e' }}
                  placeholder="Price per unit" value={returnForm.unit_price} onChange={e=>setReturnForm(f=>({...f,unit_price:e.target.value}))}/>
                {returnForm.unit_price && returnForm.quantity && (
                  <div style={{ fontSize:11, color:'#c8a96e', marginTop:3 }}>
                    Total value: {fmtKES(parseFloat(returnForm.unit_price||0) * parseFloat(returnForm.quantity||0))}
                  </div>
                )}
              </Field>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Customer Name (optional)">
                  <input style={inputStyle} placeholder="Guest or walk-in name" value={returnForm.customer_name} onChange={e=>setReturnForm(f=>({...f,customer_name:e.target.value}))}/>
                </Field>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Description * — What happened?">
                  <textarea style={{ ...inputStyle, minHeight:80, resize:'vertical' }}
                    placeholder="Describe what went wrong — complaint details, condition of item, what action was taken…"
                    value={returnForm.description} onChange={e=>setReturnForm(f=>({...f,description:e.target.value}))}/>
                </Field>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1 }} onClick={submitReturn}>Submit Return Note</button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setReturnModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Return — approve/reject action modal */}
      {actionModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:`1px solid ${actionType==='approve'?'#2ecc7144':'#e74c3c44'}`, borderRadius:6, padding:24, maxWidth:420, width:'100%', overflowX:'hidden' }}>
            <div style={{ fontSize:15, fontWeight:700, color:actionType==='approve'?'#2ecc71':'#e74c3c', marginBottom:6 }}>
              {actionType==='approve' ? '✓ Approve Return' : '✕ Reject Return'}
            </div>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:8 }}>
              <strong style={{ color:'#c8a96e' }}>{actionModal.ref}</strong> — {actionModal.item_name} × {parseFloat(actionModal.quantity)} — <strong style={{ color:'#c8a96e' }}>{fmtKES(actionModal.total_value)}</strong>
            </div>
            <div style={{ fontSize:12, color:'var(--textFaint)', marginBottom:14, padding:'8px 10px', background:'var(--bgCard)', borderRadius:3 }}>
              Reason: {actionModal.reason_label} · Source: {actionModal.source_label}<br/>
              Filed by: {actionModal.created_by_name} · {new Date(actionModal.created_at).toLocaleString('en-KE')}
            </div>
            <Field label={actionType==='approve' ? 'Resolution / Action Taken (optional)' : 'Rejection Reason *'}>
              <textarea style={{ ...inputStyle, minHeight:70, resize:'vertical' }}
                placeholder={actionType==='approve' ? 'e.g. Full refund issued, item replaced, credited to folio…' : 'Why is this return being rejected?'}
                value={actionResolution} onChange={e=>setActionResolution(e.target.value)} autoFocus/>
            </Field>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button style={{ ...btnPrimary, marginTop:0, flex:1, background:actionType==='approve'?'#1a6b3a':'#6b1a1a' }}
                onClick={async () => {
                  if (actionType==='reject' && !actionResolution.trim()) { notify('Rejection reason required','error'); return; }
                  try {
                    if (actionType==='approve') await api.approveReturn(actionModal.id, { resolution: actionResolution });
                    else await api.rejectReturn(actionModal.id, { resolution: actionResolution });
                    setActionModal(null); setActionResolution('');
                    await load();
                    notify(`Return ${actionType === 'approve' ? 'approved' : 'rejected'} and logged`);
                  } catch(e) { notify(e.response?.data?.error || 'Error','error'); }
                }}>
                {actionType==='approve' ? '✓ Confirm Approval' : '✕ Confirm Rejection'}
              </button>
              <button style={{ ...btnSm, padding:'11px 18px' }} onClick={()=>setActionModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Return detail modal */}
      {returnDetail && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--bgCard)', border:'1px solid #2a2a2a', borderRadius:6, padding:24, maxWidth:500, width:'100%', overflowX:'hidden', maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:11, color:'#c8a96e', letterSpacing:2, textTransform:'uppercase' }}>Return Note</div>
                <div style={{ fontSize:18, fontWeight:700, color:'var(--textPrimary)', marginTop:4 }}>{returnDetail.ref}</div>
              </div>
              <button onClick={()=>setReturnDetail(null)} style={{ background:'none', border:'none', color:'var(--textFaint)', fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'8px 14px', marginBottom:14 }}>
              {[
                ['Item', returnDetail.item_name],
                ['Quantity', `×${parseFloat(returnDetail.quantity)}`],
                ['Unit Price', fmtKES(returnDetail.unit_price)],
                ['Total Value', fmtKES(returnDetail.total_value)],
                ['Source', returnDetail.source_label],
                ['Reason', returnDetail.reason_label],
                ['Customer', returnDetail.customer_name||'—'],
                ['Logged By', returnDetail.created_by_name],
                ['Date', new Date(returnDetail.created_at).toLocaleString('en-KE')],
                ['Status', returnDetail.status_label],
              ].map(([l,v])=>(
                <div key={l}>
                  <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:13, color:'var(--textSecond)' }}>{v}</div>
                </div>
              ))}
            </div>
            {returnDetail.description && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:5 }}>Description</div>
                <div style={{ background:'var(--bgCard)', borderRadius:3, padding:'10px 12px', fontSize:13, color:'var(--textMuted)', lineHeight:1.5 }}>{returnDetail.description}</div>
              </div>
            )}
            {returnDetail.resolution && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:9, color:'var(--textFaint)', textTransform:'uppercase', letterSpacing:1, marginBottom:5 }}>Resolution</div>
                <div style={{ background:'#0a180a', border:'1px solid #2ecc7133', borderRadius:3, padding:'10px 12px', fontSize:13, color:'#2ecc71', lineHeight:1.5 }}>{returnDetail.resolution}</div>
              </div>
            )}
            {returnDetail.actioned_by_name && returnDetail.actioned_by_name!=='—' && (
              <div style={{ fontSize:12, color:'var(--textFaint)', marginBottom:12 }}>
                {returnDetail.status==='approved'?'Approved':'Actioned'} by <strong style={{ color:'var(--textMuted)' }}>{returnDetail.actioned_by_name}</strong>
                {returnDetail.actioned_at && ` on ${new Date(returnDetail.actioned_at).toLocaleString('en-KE')}`}
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              {returnDetail.status==='pending' && (
                <>
                  <button style={{ ...btnSm, flex:1, color:'#2ecc71', background:'#0a180a', border:'1px solid #2ecc7133' }}
                    onClick={()=>{ setActionModal(returnDetail); setActionType('approve'); setActionResolution(''); setReturnDetail(null); }}>✓ Approve</button>
                  <button style={{ ...btnSm, flex:1, color:'#e74c3c', background:'#1a0a0a', border:'1px solid #e74c3c33' }}
                    onClick={()=>{ setActionModal(returnDetail); setActionType('reject'); setActionResolution(''); setReturnDetail(null); }}>✕ Reject</button>
                </>
              )}
              <button style={{ ...btnSm, padding:'9px 18px' }} onClick={()=>setReturnDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}